import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { getDomainFilterDefaults } from "../context/envDefaults";
import { useDomainFilters } from "../hooks/useFilter";
import {
	COUNTRY_CODES_MAP,
	getCountryInfo,
	groupCountriesByRegion,
	searchCountries,
	sortRegions,
} from "./CountryCodeUtils";
import { ChevronDownIcon, XIcon } from "./Icons";
import { SectionHeader } from "./SectionHeader";

// Custom debounce utility
const debounce = (func, wait) => {
	let timeout;
	return function executedFunction(...args) {
		const later = () => {
			clearTimeout(timeout);
			func(...args);
		};
		clearTimeout(timeout);
		timeout = setTimeout(later, wait);
	};
};

const DraggableSlider = React.memo(
	({
		label,
		value,
		min = 0,
		max = 100,
		step = 1,
		unit = "",
		onChange,
		formatValue,
	}) => {
		const [isDragging, setIsDragging] = useState(false);
		const [displayValue, setDisplayValue] = useState(value);
		const [animationId, setAnimationId] = useState(null);
		const sliderRef = useRef(null);
		const dragStartValueRef = useRef(value);

		// Update display value when prop changes (but only if not dragging)
		useEffect(() => {
			if (!isDragging) {
				setDisplayValue(value);
			}
		}, [value, isDragging]);

		const calculateValue = useCallback(
			(clientX) => {
				if (!sliderRef.current) return value;

				const rect = sliderRef.current.getBoundingClientRect();
				const percent = Math.max(
					0,
					Math.min(1, (clientX - rect.left) / rect.width),
				);
				const newValue = min + percent * (max - min);
				const steppedValue = Math.round(newValue / step) * step;
				return Math.max(min, Math.min(max, steppedValue));
			},
			[min, max, step, value],
		);

		// Smooth visual update using requestAnimationFrame
		const updateVisualValue = useCallback(
			(newValue) => {
				if (animationId) {
					cancelAnimationFrame(animationId);
				}

				const id = requestAnimationFrame(() => {
					setDisplayValue(newValue);
				});

				setAnimationId(id);
			},
			[animationId],
		);

		// Keyboard support
		const handleKeyDown = useCallback(
			(e) => {
				if (!sliderRef.current) return;

				let newValue = displayValue;
				const smallStep = step;
				const largeStep = Math.max(step * 10, (max - min) / 10);

				switch (e.key) {
					case "ArrowRight":
					case "ArrowUp":
						e.preventDefault();
						newValue = Math.min(max, displayValue + smallStep);
						break;
					case "ArrowLeft":
					case "ArrowDown":
						e.preventDefault();
						newValue = Math.max(min, displayValue - smallStep);
						break;
					case "PageUp":
						e.preventDefault();
						newValue = Math.min(max, displayValue + largeStep);
						break;
					case "PageDown":
						e.preventDefault();
						newValue = Math.max(min, displayValue - largeStep);
						break;
					case "Home":
						e.preventDefault();
						newValue = min;
						break;
					case "End":
						e.preventDefault();
						newValue = max;
						break;
					default:
						return;
				}

				// Round to nearest step
				newValue = Math.round(newValue / step) * step;
				newValue = Math.max(min, Math.min(max, newValue));

				setDisplayValue(newValue);
				onChange(newValue);
			},
			[displayValue, min, max, step, onChange],
		);

		const handleMouseDown = useCallback(
			(e) => {
				// Only handle left mouse button
				if (e.button !== 0) return;

				e.preventDefault();
				e.stopPropagation();

				setIsDragging(true);
				dragStartValueRef.current = value;

				// Disable text selection during drag
				document.body.style.userSelect = "none";
				document.body.style.webkitUserSelect = "none";

				const newValue = calculateValue(e.clientX);
				updateVisualValue(newValue);

				const handleMouseMove = (moveEvent) => {
					moveEvent.preventDefault();
					moveEvent.stopPropagation();

					const currentValue = calculateValue(moveEvent.clientX);
					updateVisualValue(currentValue);
				};

				const handleMouseUp = (upEvent) => {
					upEvent.preventDefault();
					upEvent.stopPropagation();

					// Re-enable text selection
					document.body.style.userSelect = "";
					document.body.style.webkitUserSelect = "";

					setIsDragging(false);

					// Final value calculation and callback
					const finalValue = calculateValue(upEvent.clientX);
					setDisplayValue(finalValue);

					// Only call onChange if value actually changed
					if (finalValue !== dragStartValueRef.current) {
						onChange(finalValue);
					}

					// Clean up event listeners
					document.removeEventListener("mousemove", handleMouseMove, {
						passive: false,
					});
					document.removeEventListener("mouseup", handleMouseUp);

					// Cancel any pending animation frame
					if (animationId) {
						cancelAnimationFrame(animationId);
						setAnimationId(null);
					}
				};

				// Use passive: false to ensure preventDefault works
				document.addEventListener("mousemove", handleMouseMove, {
					passive: false,
				});
				document.addEventListener("mouseup", handleMouseUp);
			},
			[calculateValue, onChange, value, updateVisualValue, animationId],
		);

		const handleThumbMouseDown = useCallback(
			(e) => {
				// Stop event from bubbling to track
				e.stopPropagation();
				handleMouseDown(e);
			},
			[handleMouseDown],
		);

		const handleTrackClick = useCallback(
			(e) => {
				// Only handle clicks directly on the track, not on thumb
				if (e.target === e.currentTarget && !isDragging) {
					e.preventDefault();
					e.stopPropagation();

					const newValue = calculateValue(e.clientX);
					setDisplayValue(newValue);
					onChange(newValue);
				}
			},
			[calculateValue, onChange, isDragging],
		);

		// Cleanup on unmount
		useEffect(() => {
			return () => {
				if (animationId) {
					cancelAnimationFrame(animationId);
				}
				// Restore text selection if component unmounts during drag
				document.body.style.userSelect = "";
				document.body.style.webkitUserSelect = "";
			};
		}, [animationId]);

		const percentage = ((displayValue - min) / (max - min)) * 100;
		const formattedValue = formatValue
			? formatValue(displayValue)
			: `${displayValue}${unit}`;

		return (
			<div className="enhanced-slider-container">
				<label className="enhanced-slider-label">
					{label}: {formattedValue}
				</label>

				<div className="enhanced-slider-wrapper">
					<div
						ref={sliderRef}
						className={`enhanced-slider-track ${isDragging ? "dragging" : ""}`}
						onClick={handleTrackClick}
						onMouseDown={handleMouseDown}
						onKeyDown={handleKeyDown}
						role="slider"
						aria-valuemin={min}
						aria-valuemax={max}
						aria-valuenow={displayValue}
						aria-label={label}
						tabIndex={0}
					>
						{/* Progress fill */}
						<div
							className="enhanced-slider-fill"
							style={{ width: `${percentage}%` }}
						/>

						{/* Thumb - now fully interactive */}
						<div
							className={`enhanced-slider-thumb ${isDragging ? "dragging" : ""}`}
							style={{ left: `${percentage}%` }}
							onMouseDown={handleThumbMouseDown}
						/>
					</div>

					{/* Range labels */}
					<div className="enhanced-slider-labels">
						<span>
							{min}
							{unit}
						</span>
						<span>
							{max}
							{unit}
						</span>
					</div>
				</div>
			</div>
		);
	},
);

// Custom debounce hook
const useDebounce = (value, delay) => {
	const [debouncedValue, setDebouncedValue] = useState(value);

	useEffect(() => {
		const handler = setTimeout(() => {
			setDebouncedValue(value);
		}, delay);

		return () => clearTimeout(handler);
	}, [value, delay]);

	return debouncedValue;
};

const BurgerIcon = () => (
	<svg
		width="20"
		height="20"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
	>
		<line x1="3" y1="6" x2="21" y2="6"></line>
		<line x1="3" y1="12" x2="21" y2="12"></line>
		<line x1="3" y1="18" x2="21" y2="18"></line>
	</svg>
);

const UploadIcon = () => (
	<svg
		width="14"
		height="14"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
	>
		<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
		<polyline points="17,8 12,3 7,8"></polyline>
		<line x1="12" y1="3" x2="12" y2="15"></line>
	</svg>
);

const RemoveIcon = () => (
	<svg
		width="12"
		height="12"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
	>
		<line x1="18" y1="6" x2="6" y2="18"></line>
		<line x1="6" y1="6" x2="18" y2="18"></line>
	</svg>
);

// Memoized SearchInput component with better performance
const SearchInput = React.memo(
	({
		value,
		onChange,
		placeholder,
		className,
		autoComplete = "off",
		style,
	}) => {
		return (
			<input
				type="text"
				value={value}
				onChange={onChange}
				placeholder={placeholder}
				className={className}
				autoComplete={autoComplete}
				style={style}
			/>
		);
	},
);

// Memoized LimitInput component to prevent unnecessary re-renders
const LimitInput = React.memo(({ value, onChange }) => {
	const [localValue, setLocalValue] = useState(value || 1000);

	// Update local value when prop changes
	useEffect(() => {
		setLocalValue(value || 1000);
	}, [value]);

	const handleChange = useCallback((e) => {
		const newValue = e.target.value;
		setLocalValue(newValue);
	}, []);

	const handleBlur = useCallback(() => {
		const numValue = parseInt(localValue, 10);
		const finalValue = isNaN(numValue) ? 1000 : Math.max(1, numValue);
		if (finalValue !== value) {
			onChange(finalValue);
		}
	}, [localValue, value, onChange]);

	const handleKeyPress = useCallback(
		(e) => {
			if (e.key === "Enter") {
				handleBlur();
			}
		},
		[handleBlur],
	);

	return (
		<input
			type="number"
			value={localValue}
			onChange={handleChange}
			onBlur={handleBlur}
			onKeyPress={handleKeyPress}
			placeholder="1000"
			min="1"
			style={{
				width: "100%",
				padding: "6px 8px",
				border: "1px solid #d1d5db",
				borderRadius: "4px",
				fontSize: "11px",
				backgroundColor: "white",
				color: "#374151",
				boxSizing: "border-box",
			}}
		/>
	);
});

// Custom Filter Input Component to fix refreshing issue
const CustomFilterInput = React.memo(({ customFilter, onUpdate }) => {
	// Columns that are multiplied by 10^6
	const columnsToMultiply = [
		"nonzerobidsvalueinusd",
		"bidspendinusd",
		"revenueperimpression",
		"costperimpression",
		"bid_guidance",
		"bidCpm",
	];

	// Display value (divide by 10^6 if it's one of the special columns)
	const displayValue = columnsToMultiply.includes(customFilter.column)
		? (customFilter.threshold || 0) / 10 ** 6
		: customFilter.threshold || 0;

	const [localValue, setLocalValue] = useState(displayValue);

	useEffect(() => {
		setLocalValue(displayValue);
	}, [displayValue]);

	const handleChange = useCallback((e) => {
		setLocalValue(e.target.value);
	}, []);

	const handleBlur = useCallback(() => {
		const value = localValue === "" ? 0 : parseFloat(localValue);
		const finalValue = isNaN(value) ? 0 : value;

		// Pass the original value (without multiplication) - multiplication happens in updateCustomFilter
		if (finalValue !== displayValue) {
			onUpdate(customFilter.id, "threshold", finalValue);
		}
	}, [localValue, displayValue, customFilter.id, onUpdate]);

	const handleKeyPress = useCallback(
		(e) => {
			if (e.key === "Enter") {
				handleBlur();
			}
		},
		[handleBlur],
	);

	return (
		<input
			type="number"
			value={localValue}
			onChange={handleChange}
			onBlur={handleBlur}
			onKeyPress={handleKeyPress}
			placeholder="0"
			step="0.000001"
			style={{
				width: "100%",
				padding: "4px 6px",
				fontSize: "10px",
				border: "1px solid #ddd",
				borderRadius: "3px",
				backgroundColor: "white",
				boxSizing: "border-box",
			}}
		/>
	);
});

export const FiltersSidebar = React.memo(
	({
		showFilters,
		filters: propFilters,
		expandedSections: propExpandedSections,
		onToggleSection,
		onUpdateFilter,
		onToggleArrayFilter,
		onClearAllFilters,
		onFiltersChange,
		onClose,
		activeTab,
		userId,
		enablePresets = false,
		enableRecommendations = false,
		onUploadIncludeList,
		onUploadBlockedList,
	}) => {
		// ✅ NEW: Scroll position preservation
		const filtersContentRef = useRef(null);
		const savedScrollPositions = useRef({});

		// ✅ NEW: Save scroll position before re-render
		const saveScrollPosition = useCallback(() => {
			if (filtersContentRef.current) {
				savedScrollPositions.current.main = filtersContentRef.current.scrollTop;
			}

			// Save scroll positions for individual sections
			const scrollableElements = filtersContentRef.current?.querySelectorAll(
				".countries-by-region, .search-results-list, .categories-list, .publisher-list",
			);
			scrollableElements?.forEach((element, index) => {
				savedScrollPositions.current[`section_${index}`] = element.scrollTop;
			});
		}, []);

		// ✅ NEW: Restore scroll position after re-render
		const restoreScrollPosition = useCallback(() => {
			// Use setTimeout to ensure DOM has updated
			setTimeout(() => {
				if (
					filtersContentRef.current &&
					savedScrollPositions.current.main !== undefined
				) {
					filtersContentRef.current.scrollTop =
						savedScrollPositions.current.main;
				}

				// Restore scroll positions for individual sections
				const scrollableElements = filtersContentRef.current?.querySelectorAll(
					".countries-by-region, .search-results-list, .categories-list, .publisher-list",
				);
				scrollableElements?.forEach((element, index) => {
					if (savedScrollPositions.current[`section_${index}`] !== undefined) {
						element.scrollTop =
							savedScrollPositions.current[`section_${index}`];
					}
				});
			}, 0);
		}, []);

		const {
			filterValues: apiFilterValues,
			safetyFilters,
			isLoading,
			error,
			isAuthenticated,
			needsAuth,
			isAuthError,
			authAttempted,
			retry,
			refresh,
			signIn,
			user,
			loadFilters,
		} = useDomainFilters();

		const [localFilters, setLocalFilters] = useState(getDomainFilterDefaults());

		// Upload state management for simultaneous uploads
		const [uploadStates, setUploadStates] = useState({
			includeList: {
				isUploading: false,
				fileName: null,
				domains: [],
				error: null,
				isUploaded: false,
			},
			blockedList: {
				isUploading: false,
				fileName: null,
				domains: [],
				error: null,
				isUploaded: false,
			},
		});

		const [showUploadTooltip, setShowUploadTooltip] = useState(false);

		const availableColumns = [
			{ value: "cprmarketrequests", label: "Market Requests" },
			{ value: "impressions", label: "Total Impressions" },
			{ value: "viewables", label: "Viewables" },
			{ value: "viewabilitymeasures", label: "Viewability Measures" },
			{ value: "completefirstoccurrence", label: "Video Completions" },
			{ value: "startfirstoccurrence", label: "Video Starts" },
			{ value: "cprclicks", label: "CPR Clicks" },
			{ value: "fills", label: "Fills" },
			{ value: "nonzerobids", label: "Non Zero Bids" },
			{ value: "dealvolumedealinfo", label: "Deal Volume Deal Info" },
			{ value: "nonzerobidsvalueinusd", label: "Non Zero Bids Value (USD)" },
			{ value: "bidspendinusd", label: "Bid Spend (USD)" },
			{ value: "ctr", label: "CTR" },
			{ value: "vtr", label: "VTR" },
			{ value: "revenueperimpression", label: "Revenue Per Impression" },
			{ value: "costperimpression", label: "Cost Per Impression" },
			{ value: "bid_guidance", label: "Bid Guidance" },
			{ value: "bidCpm", label: "CPM" },
			{
				value: "impressionswithclickmacro",
				label: "Impressions with Click Macro",
			},
			{
				value: "cprmarketdemandpartnerspendinusd",
				label: "CPR Market Demand Partner Spend (USD)",
			},
			{
				value: "cprmarketpublisherrevenueinusd",
				label: "CPR Market Publisher Revenue (USD)",
			},
			{ value: "cprallrequests", label: "CPR All Requests" },
			{
				value: "cprprivatemarketimpressions",
				label: "CPR Private Market Impressions",
			},
		];

		const [localExpandedSections, setLocalExpandedSections] = useState({
			mediaSubtype: false,
			deviceTypes: false,
			videoPlacement: false,
			geography: false,
			dateRange: false,
			contentStyle: false,
			content_style_1: false,
			content_style_2: false,
			content_style_3: false,
			safetyTier: false,
			safety_tier_1: false,
			safety_tier_2: false,
			safety_tier_3: false,
			relevance: false,
			performance: false,
			directPublishers: false,
			publishers: false,
			category: false,
		});

		const [availableOptions, setAvailableOptions] = useState({
			mediaSubtype: [],
			deviceTypes: [],
			videoPlacement: [],
			contentStyle: [],
			content_style_1: [],
			content_style_2: [],
			content_style_3: [],
			safetyTier: [],
			safety_tier_1: [],
			safety_tier_2: [],
			safety_tier_3: [],
			userGeoCountries: [],
			iabDistributionChannels: [],
			categories: [],
			iab_category: [],
			publishers: [],
		});

		// Separate search input states for immediate UI updates
		const [publisherSearchInput, setPublisherSearchInput] = useState("");
		const [categorySearchInput, setCategorySearchInput] = useState("");
		const [countrySearchInput, setCountrySearchInput] = useState("");

		// Debounced search values for actual filtering
		const debouncedPublisherSearch = useDebounce(publisherSearchInput, 300);
		const debouncedCategorySearch = useDebounce(categorySearchInput, 300);
		const debouncedCountrySearch = useDebounce(countrySearchInput, 300);

		const [expandedCategories, setExpandedCategories] = useState({
			targeting: false,
			dateRange: false,
			content: false,
			category: false,
			performance: false,
			publishers: false,
		});

		const [expandedCategoryGroups, setExpandedCategoryGroups] = useState({});
		const [expandedCountryGroups, setExpandedCountryGroups] = useState({});

		const filters = propFilters || localFilters;
		const expandedSections = propExpandedSections || localExpandedSections;
		const [processedCategories, setProcessedCategories] = useState({});
		const [searchableCategories, setSearchableCategories] = useState([]);

		// ✅ NEW: Restore scroll position after filter changes
		useEffect(() => {
			restoreScrollPosition();
		}, [filters, restoreScrollPosition]);

		// ✅ NEW: Debounced scroll restoration to handle rapid changes
		const debouncedRestoreScrollPosition = useCallback(
			debounce(() => {
				restoreScrollPosition();
			}, 50),
			[restoreScrollPosition],
		);

		// ✅ NEW: Alternative effect for rapid filter changes
		useEffect(() => {
			debouncedRestoreScrollPosition();
		}, [
			filters?.mediaSubtype,
			filters?.deviceTypes,
			filters?.selectedCountries,
			filters?.categories,
			debouncedRestoreScrollPosition,
		]);

		// Ensure default custom filters are always present
		const ensuredFilters = {
			...filters,
			customFilters:
				filters.customFilters && filters.customFilters.length > 0
					? filters.customFilters
					: [
							{
								id: Date.now(),
								column: "cprmarketrequests",
								threshold: 10000,
							},
							{
								id: Date.now() + 1,
								column: "impressions",
								threshold: 100,
							},
						],
		};

		useEffect(() => {
			if (apiFilterValues && !isLoading && isAuthenticated) {
				const domainFilters = apiFilterValues.domainFilterValues;

				if (!domainFilters) {
					return;
				}

				setAvailableOptions((prev) => {
					const updated = { ...prev };

					if (
						domainFilters.iabMediaSubtypes &&
						Array.isArray(domainFilters.iabMediaSubtypes)
					) {
						updated.mediaSubtype = domainFilters.iabMediaSubtypes.filter(
							(item) => item !== null,
						);
					}

					if (
						domainFilters.deviceTypes &&
						Array.isArray(domainFilters.deviceTypes)
					) {
						updated.deviceTypes = domainFilters.deviceTypes.filter(
							(item) => item !== null,
						);
					}

					if (
						domainFilters.videoPlacements &&
						Array.isArray(domainFilters.videoPlacements)
					) {
						updated.videoPlacement = domainFilters.videoPlacements.filter(
							(item) => item !== null,
						);
					}

					const publisherFieldNames = [
						"publishers",
						"publisher",
						"publisherList",
						"publisher_list",
						"publisherNames",
						"publisher_names",
						"domains",
						"domain",
						"domainList",
						"domain_list",
						"domainNames",
						"domain_names",
						"sites",
						"site",
						"siteList",
						"site_list",
						"siteNames",
						"site_names",
						"publisherDomains",
						"publisher_domains",
						"siteDomains",
						"site_domains",
						"websiteList",
						"website_list",
						"websites",
						"website",
					];

					for (const fieldName of publisherFieldNames) {
						if (
							domainFilters[fieldName] &&
							Array.isArray(domainFilters[fieldName]) &&
							domainFilters[fieldName].length > 0
						) {
							updated.publishers = domainFilters[fieldName].filter(
								(item) => item !== null && item !== undefined,
							);
							break;
						}
					}

					const categoryFieldNames = [
						"iabCategories",
						"iab_categories",
						"iabCategory",
						"iab_category",
						"categories",
						"category",
						"categoryList",
						"category_list",
						"topics",
						"topic",
						"topicList",
						"topic_list",
						"verticals",
						"vertical",
						"verticalList",
						"vertical_list",
						"iabTopics",
						"iab_topics",
						"iabVerticals",
						"iab_verticals",
						"contentCategories",
						"content_categories",
						"adCategories",
						"ad_categories",
					];

					for (const fieldName of categoryFieldNames) {
						if (
							domainFilters[fieldName] &&
							Array.isArray(domainFilters[fieldName]) &&
							domainFilters[fieldName].length > 0
						) {
							updated.categories = domainFilters[fieldName].filter(
								(item) => item !== null && item !== undefined,
							);
							updated.iab_category = updated.categories;
							break;
						}
					}

					const contentStyleArrays = [
						domainFilters.content_style,
						domainFilters.content_style_1,
						domainFilters.content_style_2,
						domainFilters.content_style_3,
						domainFilters.contentStyles,
						domainFilters.content_styles,
					].filter((array) => Array.isArray(array));

					if (contentStyleArrays.length > 0) {
						const allContentStyles = contentStyleArrays
							.flat()
							.filter(
								(item) => item !== null && item !== undefined && item !== "",
							)
							.filter((item, index, arr) => arr.indexOf(item) === index)
							.sort();

						updated.contentStyle = allContentStyles;
					}

					["content_style_1", "content_style_2", "content_style_3"].forEach(
						(field) => {
							if (domainFilters[field] && Array.isArray(domainFilters[field])) {
								updated[field] = domainFilters[field].filter(
									(item) => item !== null,
								);
							}
						},
					);

					const safetyTierArrays = [
						domainFilters.safety_tier_1,
						domainFilters.safety_tier_2,
						domainFilters.safety_tier_3,
						domainFilters.safetyTiers,
						domainFilters.safety_tiers,
						domainFilters.safetyLevels,
						domainFilters.safety_levels,
						domainFilters.safetyTierOptions,
						domainFilters.safetyTier,
					].filter((array) => Array.isArray(array));

					if (safetyTierArrays.length > 0) {
						const allSafetyTiers = safetyTierArrays
							.flat()
							.filter(
								(item) => item !== null && item !== undefined && item !== "",
							)
							.filter((item, index, arr) => arr.indexOf(item) === index)
							.sort();

						updated.safetyTier = allSafetyTiers;
					}

					const countryFieldNames = [
						"userGeoCountries",
						"countries",
						"geoCountries",
						"geo_countries",
						"countryList",
						"country_list",
					];
					for (const fieldName of countryFieldNames) {
						if (
							domainFilters[fieldName] &&
							Array.isArray(domainFilters[fieldName])
						) {
							updated.userGeoCountries = domainFilters[fieldName].filter(
								(item) => item !== null,
							);
							break;
						}
					}

					[
						"content_style_1",
						"content_style_2",
						"content_style_3",
						"safety_tier_1",
						"safety_tier_2",
						"safety_tier_3",
					].forEach((field) => {
						if (domainFilters[field] && Array.isArray(domainFilters[field])) {
							updated[field] = domainFilters[field].filter(
								(item) => item !== null,
							);
						}
					});

					return updated;
				});
			}
		}, [apiFilterValues, isLoading, isAuthenticated]);

		const processCategoriesFromAPI = useCallback((apiCategories) => {
			if (!apiCategories || !Array.isArray(apiCategories)) return {};
			const cleanCategories = apiCategories
				.filter((cat) => cat && cat.trim() && cat !== null && cat !== "null")
				.map((cat) => cat.trim())
				.filter((cat, index, arr) => arr.indexOf(cat) === index);

			const categoryGroups = {
				"Arts & Entertainment": [],
				Automotive: [],
				"Business & Finance": [],
				Education: [],
				"Food & Drink": [],
				"Health & Fitness": [],
				"Hobbies & Interests": [],
				"Home & Garden": [],
				"News & Politics": [],
				"Science & Technology": [],
				Sports: [],
				Travel: [],
				Lifestyle: [],
				Other: [],
			};

			const categorizeKeywords = {
				"Arts & Entertainment": [
					"art",
					"music",
					"movie",
					"film",
					"tv",
					"entertainment",
					"comedy",
					"drama",
					"anime",
					"manga",
					"celebrity",
					"culture",
					"theater",
					"dance",
					"painting",
				],
				Automotive: [
					"auto",
					"car",
					"vehicle",
					"motorcycle",
					"truck",
					"driving",
					"racing",
					"motor",
				],
				"Business & Finance": [
					"business",
					"finance",
					"money",
					"investment",
					"banking",
					"economy",
					"marketing",
					"career",
					"job",
					"work",
				],
				Education: [
					"education",
					"school",
					"college",
					"university",
					"learning",
					"student",
					"academic",
					"study",
				],
				"Food & Drink": [
					"food",
					"recipe",
					"cooking",
					"drink",
					"restaurant",
					"cuisine",
					"dining",
					"nutrition",
					"diet",
				],
				"Health & Fitness": [
					"health",
					"fitness",
					"medical",
					"wellness",
					"exercise",
					"medicine",
					"mental health",
					"yoga",
					"therapy",
				],
				"Home & Garden": [
					"home",
					"garden",
					"diy",
					"decor",
					"furniture",
					"interior",
					"cleaning",
					"plant",
					"craft",
				],
				"News & Politics": [
					"news",
					"politics",
					"government",
					"election",
					"current",
					"affairs",
					"policy",
				],
				"Science & Technology": [
					"technology",
					"science",
					"computer",
					"software",
					"digital",
					"tech",
					"internet",
					"app",
					"gadget",
					"ai",
					"cyber",
				],
				Sports: [
					"sport",
					"football",
					"basketball",
					"tennis",
					"golf",
					"baseball",
					"soccer",
					"olympic",
					"fitness",
				],
				Travel: [
					"travel",
					"destination",
					"vacation",
					"hotel",
					"flight",
					"tourism",
					"trip",
					"cruise",
				],
				Lifestyle: [
					"lifestyle",
					"fashion",
					"beauty",
					"relationship",
					"family",
					"parenting",
					"love",
					"personal",
				],
			};

			cleanCategories.forEach((category) => {
				const categoryLower = category.toLowerCase();
				let placed = false;
				for (const [groupName, keywords] of Object.entries(
					categorizeKeywords,
				)) {
					if (keywords.some((keyword) => categoryLower.includes(keyword))) {
						categoryGroups[groupName].push(category);
						placed = true;
						break;
					}
				}
				if (!placed) {
					categoryGroups["Other"].push(category);
				}
			});

			Object.keys(categoryGroups).forEach((key) => {
				if (categoryGroups[key].length === 0) {
					delete categoryGroups[key];
				}
			});

			return categoryGroups;
		}, []);

		useEffect(() => {
			if (
				availableOptions.categories &&
				availableOptions.categories.length > 0
			) {
				const processed = processCategoriesFromAPI(availableOptions.categories);
				setProcessedCategories(processed);

				const flatList = availableOptions.categories
					.filter((cat) => cat && cat.trim() && cat !== null && cat !== "null")
					.map((cat) => cat.trim())
					.filter((cat, index, arr) => arr.indexOf(cat) === index)
					.sort();

				setSearchableCategories(flatList);
			}
		}, [availableOptions.categories, processCategoriesFromAPI]);

		const isParentCategoryToggleRef = useRef(false);

		const handleToggleSection = useCallback(
			(sectionName) => {
				if (onToggleSection) {
					onToggleSection(sectionName);
				} else {
					setLocalExpandedSections((prev) => ({
						...prev,
						[sectionName]: !prev[sectionName],
					}));
				}
			},
			[onToggleSection],
		);

		const handleToggleCategory = useCallback((categoryName) => {
			isParentCategoryToggleRef.current = true;

			setExpandedCategories((prev) => {
				const newExpanded = !prev[categoryName];
				const updated = { ...prev, [categoryName]: newExpanded };
				return updated;
			});

			setTimeout(() => {
				isParentCategoryToggleRef.current = false;
			}, 100);
		}, []);

		useEffect(() => {
			if (!isParentCategoryToggleRef.current) {
				return;
			}

			if (expandedCategories.targeting) {
				const sectionsToExpand = [
					"mediaSubtype",
					"deviceTypes",
					"videoPlacement",
					"geography",
				];
				sectionsToExpand.forEach((section) => {
					if (!expandedSections[section]) {
						if (onToggleSection) {
							setTimeout(() => onToggleSection(section), 50);
						} else {
							setLocalExpandedSections((prev) => ({
								...prev,
								[section]: true,
							}));
						}
					}
				});
			} else {
				const sectionsToCollapse = [
					"mediaSubtype",
					"deviceTypes",
					"videoPlacement",
					"geography",
				];
				sectionsToCollapse.forEach((section) => {
					if (expandedSections[section]) {
						if (onToggleSection) {
							setTimeout(() => onToggleSection(section), 50);
						} else {
							setLocalExpandedSections((prev) => ({
								...prev,
								[section]: false,
							}));
						}
					}
				});
			}

			if (expandedCategories.content) {
				const sectionsToExpand = ["contentStyle", "safetyTier"];
				sectionsToExpand.forEach((section) => {
					if (!expandedSections[section]) {
						if (onToggleSection) {
							setTimeout(() => onToggleSection(section), 50);
						} else {
							setLocalExpandedSections((prev) => ({
								...prev,
								[section]: true,
							}));
						}
					}
				});
			} else {
				const sectionsToCollapse = ["contentStyle", "safetyTier"];
				sectionsToCollapse.forEach((section) => {
					if (expandedSections[section]) {
						if (onToggleSection) {
							setTimeout(() => onToggleSection(section), 50);
						} else {
							setLocalExpandedSections((prev) => ({
								...prev,
								[section]: false,
							}));
						}
					}
				});
			}
		}, [
			expandedCategories.targeting,
			expandedCategories.content,
			expandedSections,
			onToggleSection,
		]);

		const CountrySearchInput = ({ value, onChange, onClear }) => {
			const inputRef = useRef(null);
			const callbackRef = useRef(onChange);
			// Update callback ref without causing re-renders
			useEffect(() => {
				callbackRef.current = onChange;
			}, [onChange]);
			// Only sync value if input is not focused (user not typing)
			useEffect(() => {
				if (inputRef.current && !inputRef.current.matches(":focus")) {
					inputRef.current.value = value || "";
				}
			}, [value]);
			return (
				<div
					className="search-container"
					style={{ position: "relative", marginBottom: "12px" }}
				>
					<input
						ref={inputRef}
						type="text"
						defaultValue={value || ""} // Use defaultValue, NOT value
						onInput={(e) => {
							// Call parent directly without React state updates
							if (callbackRef.current) {
								const mockEvent = {
									target: { value: e.target.value },
									preventDefault: () => {},
									stopPropagation: () => {},
								};
								callbackRef.current(mockEvent);
							}
						}}
						placeholder="Search countries..."
						className="search-input"
						autoComplete="off"
						style={{
							width: "100%",
							padding: "6px 25px 6px 8px",
							border: "1px solid #d1d5db",
							borderRadius: "4px",
							fontSize: "11px",
							outline: "none",
							backgroundColor: "white",
							color: "#333",
							caretColor: "#0076BD",
							// Nuclear-style cursor fixes
							transform: "translateZ(0)",
							willChange: "auto",
							backfaceVisibility: "visible",
							WebkitTextFillColor: "#333",
							WebkitOpacity: 1,
						}}
						onFocus={(e) => {
							e.target.style.borderColor = "#0076BD";
							e.target.style.boxShadow = "0 0 0 2px rgba(0, 118, 189, 0.2)";
							e.target.style.caretColor = "#0076BD";
						}}
						onBlur={(e) => {
							e.target.style.borderColor = "#d1d5db";
							e.target.style.boxShadow = "none";
						}}
					/>
					{value && (
						<button
							onClick={() => {
								if (inputRef.current) {
									inputRef.current.value = "";
									inputRef.current.focus();
								}
								onClear && onClear();
							}}
							className="clear-search-btn"
							type="button"
							style={{
								position: "absolute",
								right: "6px",
								top: "50%",
								transform: "translateY(-50%)",
								background: "none",
								border: "none",
								color: "#6b7280",
								cursor: "pointer",
								fontSize: "16px",
								padding: "2px",
							}}
						>
							×
						</button>
					)}
				</div>
			);
		};

		const PublisherSearchInput = ({ value, onChange }) => {
			const inputRef = useRef(null);
			const callbackRef = useRef(onChange);
			// Update callback ref without causing re-renders
			useEffect(() => {
				callbackRef.current = onChange;
			}, [onChange]);
			// Only sync value if input is not focused (user not typing)
			useEffect(() => {
				if (inputRef.current && !inputRef.current.matches(":focus")) {
					inputRef.current.value = value || "";
				}
			}, [value]);
			return (
				<input
					ref={inputRef}
					type="text"
					defaultValue={value || ""} // Use defaultValue, NOT value
					onInput={(e) => {
						// Call parent directly without React state updates
						if (callbackRef.current) {
							const mockEvent = {
								target: { value: e.target.value },
								preventDefault: () => {},
								stopPropagation: () => {},
							};
							callbackRef.current(mockEvent);
						}
					}}
					placeholder="Search publishers..."
					autoComplete="off"
					style={{
						width: "100%",
						padding: "6px 8px",
						border: "1px solid #d1d5db",
						borderRadius: "6px",
						fontSize: "11px",
						backgroundColor: "white",
						color: "#333",
						marginBottom: "8px",
						outline: "none",
						caretColor: "#0076BD",
						// Nuclear-style cursor fixes
						transform: "translateZ(0)",
						willChange: "auto",
						backfaceVisibility: "visible",
						WebkitTextFillColor: "#333",
						WebkitOpacity: 1,
					}}
					onFocus={(e) => {
						e.target.style.borderColor = "#0076BD";
						e.target.style.boxShadow = "0 0 0 2px rgba(0, 118, 189, 0.2)";
						e.target.style.caretColor = "#0076BD";
					}}
					onBlur={(e) => {
						e.target.style.borderColor = "#d1d5db";
						e.target.style.boxShadow = "none";
					}}
				/>
			);
		};

		const CategorySearchInput = ({ value, onChange, onClear }) => {
			const inputRef = useRef(null);
			const callbackRef = useRef(onChange);
			// Update callback ref without causing re-renders
			useEffect(() => {
				callbackRef.current = onChange;
			}, [onChange]);
			// Only sync value if input is not focused (user not typing)
			useEffect(() => {
				if (inputRef.current && !inputRef.current.matches(":focus")) {
					inputRef.current.value = value || "";
				}
			}, [value]);
			return (
				<div
					className="category-search-container"
					style={{ position: "relative", marginBottom: "12px" }}
				>
					<input
						ref={inputRef}
						type="text"
						defaultValue={value || ""} // Use defaultValue, NOT value
						onInput={(e) => {
							// Call parent directly without any React state updates
							if (callbackRef.current) {
								// Create a mock event to satisfy the parent function
								const mockEvent = {
									target: { value: e.target.value },
									preventDefault: () => {},
									stopPropagation: () => {},
								};
								callbackRef.current(mockEvent);
							}
						}}
						placeholder="Search categories..."
						className="category-search-input"
						autoComplete="off"
						style={{
							width: "100%",
							padding: "6px 25px 6px 8px",
							border: "1px solid #d1d5db",
							borderRadius: "4px",
							fontSize: "11px",
							fontFamily: "inherit",
							outline: "none",
							backgroundColor: "white",
							color: "#333",
							caretColor: "#0076BD",
							// Nuclear-style cursor fixes
							transform: "translateZ(0)",
							willChange: "auto",
							backfaceVisibility: "visible",
							WebkitTextFillColor: "#333",
							WebkitOpacity: 1,
						}}
						onFocus={(e) => {
							e.target.style.borderColor = "#0076BD";
							e.target.style.boxShadow = "0 0 0 2px rgba(0, 118, 189, 0.2)";
							e.target.style.caretColor = "#0076BD";
						}}
						onBlur={(e) => {
							e.target.style.borderColor = "#d1d5db";
							e.target.style.boxShadow = "none";
						}}
					/>
					<button
						onClick={() => {
							if (inputRef.current) {
								inputRef.current.value = "";
								inputRef.current.focus();
							}
							onClear && onClear();
						}}
						type="button"
						style={{
							position: "absolute",
							right: "6px",
							top: "50%",
							transform: "translateY(-50%)",
							background: "none",
							border: "none",
							color: "#6b7280",
							cursor: "pointer",
							fontSize: "16px",
							padding: "2px",
						}}
					>
						×
					</button>
				</div>
			);
		};

		const handleUpdateFilter = useCallback(
			(filterKey, value) => {
				// ✅ NEW: Save scroll position before update
				saveScrollPosition();

				if (filterKey === "uploadedDomains") {
					if (onUpdateFilter) {
						onUpdateFilter(filterKey, value);
					} else {
						setLocalFilters((prev) => {
							const updated = { ...prev, [filterKey]: value };
							if (onFiltersChange) {
								onFiltersChange(updated);
							}
							return updated;
						});
					}
				} else {
					if (onUpdateFilter) {
						onUpdateFilter(filterKey, value);
					} else {
						setLocalFilters((prev) => {
							const updated = { ...prev, [filterKey]: value };
							if (onFiltersChange) {
								onFiltersChange(updated);
							}
							return updated;
						});
					}
				}
			},
			[onUpdateFilter, onFiltersChange, saveScrollPosition],
		);

		const handleToggleArrayFilter = useCallback(
			(filterKey, value) => {
				// ✅ NEW: Save scroll position before update
				saveScrollPosition();

				if (onToggleArrayFilter) {
					onToggleArrayFilter(filterKey, value);
				} else {
					setLocalFilters((prev) => {
						const currentArray = prev[filterKey] || [];
						const updated = {
							...prev,
							[filterKey]: currentArray.includes(value)
								? currentArray.filter((item) => item !== value)
								: [...currentArray, value],
						};

						if (onFiltersChange) {
							onFiltersChange(updated);
						}

						return updated;
					});
				}
			},
			[onToggleArrayFilter, onFiltersChange, saveScrollPosition],
		);

		const handleCountrySearchChange = useCallback((e) => {
			clearTimeout(window.categorySearchTimeout);
			window.categorySearchTimeout = setTimeout(() => {
				setCountrySearchInput(e.target.value);
			}, 1000);
		}, []);

		const handlePublisherSearchChange = useCallback((e) => {
			clearTimeout(window.categorySearchTimeout);
			window.categorySearchTimeout = setTimeout(() => {
				setPublisherSearchInput(e.target.value);
			}, 1000);
		}, []);

		const handleCategorySearchChange = useCallback((e) => {
			// Don't update React state at all during typing!
			console.log("Category search:", e.target.value);
			// Only update state much later to avoid interference
			clearTimeout(window.categorySearchTimeout);
			window.categorySearchTimeout = setTimeout(() => {
				setCategorySearchInput(e.target.value);
			}, 1000); // Much longer delay
		}, []);

		const clearCountrySearch = useCallback(() => {
			setCountrySearchInput("");
		}, []);

		const clearCategorySearch = useCallback(() => {
			setCategorySearchInput("");
		}, []);

		const filteredCountryCodes = useMemo(() => {
			const allCountryCodes = Object.keys(COUNTRY_CODES_MAP).filter((code) => {
				const countryInfo = getCountryInfo(code);
				return countryInfo.region && countryInfo.region.trim() !== "";
			});

			return debouncedCountrySearch.trim()
				? searchCountries(allCountryCodes, debouncedCountrySearch)
				: allCountryCodes;
		}, [debouncedCountrySearch]);

		const filteredPublishers = useMemo(() => {
			const allPublishers =
				availableOptions?.publishers?.length > 0
					? availableOptions.publishers
					: [
							"Financial Times Ltd.",
							"Dow Jones & Co.",
							"Bloomberg L.P.",
							"NBCUniversal",
							"The New York Times",
							"The Wall Street Journal",
							"Reuters",
							"Associated Press",
							"CNN",
							"BBC",
							"Fox News",
							"CNBC",
							"ESPN",
							"TIME",
							"Forbes",
						];

			return allPublishers.filter((pub) =>
				pub.toLowerCase().includes(debouncedPublisherSearch.toLowerCase()),
			);
		}, [debouncedPublisherSearch, availableOptions.publishers]);

		const filteredCategories = useMemo(() => {
			if (!debouncedCategorySearch.trim()) return null;

			return searchableCategories.filter((category) =>
				category.toLowerCase().includes(debouncedCategorySearch.toLowerCase()),
			);
		}, [debouncedCategorySearch, searchableCategories]);

		const countriesByRegion = useMemo(() => {
			return groupCountriesByRegion(filteredCountryCodes);
		}, [filteredCountryCodes]);

		const sortedRegions = useMemo(() => {
			return sortRegions(Object.keys(countriesByRegion));
		}, [countriesByRegion]);

		// CSV parsing utility function
		const parseCSV = (csvText) => {
			const lines = csvText
				.split("\n")
				.map((line) => line.trim())
				.filter((line) => line);
			const domains = [];

			for (let i = 0; i < lines.length; i++) {
				const line = lines[i];

				if (
					i === 0 &&
					(line.toLowerCase().includes("domain") ||
						line.toLowerCase().includes("website") ||
						line.toLowerCase().includes("site"))
				) {
					continue;
				}

				const values = line
					.split(",")
					.map((val) => val.trim().replace(/^["']|["']$/g, ""));

				for (const value of values) {
					if (value && isValidDomain(value)) {
						domains.push(cleanDomain(value));
						break;
					}
				}
			}

			return domains;
		};

		const isValidDomain = (domain) => {
			if (!domain || typeof domain !== "string") return false;

			const domainRegex =
				/^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

			const cleanDomainCheck = domain
				.toLowerCase()
				.replace(/^https?:\/\//, "")
				.replace(/^www\./, "")
				.replace(/\/.*$/, "")
				.trim();

			return cleanDomainCheck.length > 0 && domainRegex.test(cleanDomainCheck);
		};

		const cleanDomain = (domain) => {
			return domain
				.toLowerCase()
				.replace(/^https?:\/\//, "")
				.replace(/^www\./, "")
				.replace(/\/.*$/, "")
				.trim();
		};

		const handleFileUpload = async (file, listType) => {
			if (!file) return;

			if (!file.name.toLowerCase().endsWith(".csv")) {
				setUploadStates((prev) => ({
					...prev,
					[listType]: {
						...prev[listType],
						error: "Please upload a CSV file",
						isUploading: false,
					},
				}));
				return;
			}

			setUploadStates((prev) => ({
				...prev,
				[listType]: {
					...prev[listType],
					isUploading: true,
					error: null,
				},
			}));

			try {
				const fileContent = await new Promise((resolve, reject) => {
					const reader = new FileReader();
					reader.onload = (e) => resolve(e.target.result);
					reader.onerror = reject;
					reader.readAsText(file);
				});

				const domains = parseCSV(fileContent);

				if (domains.length === 0) {
					throw new Error("No valid domains found in the CSV file");
				}

				setUploadStates((prev) => ({
					...prev,
					[listType]: {
						isUploading: false,
						fileName: file.name,
						domains: domains,
						error: null,
						isUploaded: true,
					},
				}));

				if (listType === "includeList") {
					if (onUploadIncludeList) {
						onUploadIncludeList(domains);
					}
					handleUpdateFilter("uploadedDomains", {
						includeList: domains,
						excludeList: uploadStates.blockedList.domains || [],
					});
				} else if (listType === "blockedList") {
					if (onUploadBlockedList) {
						onUploadBlockedList(domains);
					}
					handleUpdateFilter("uploadedDomains", {
						includeList: uploadStates.includeList.domains || [],
						excludeList: domains,
					});
				}
			} catch (error) {
				console.error(`Error uploading ${listType}:`, error);
				setUploadStates((prev) => ({
					...prev,
					[listType]: {
						...prev[listType],
						isUploading: false,
						error: error.message || "Failed to parse CSV file",
					},
				}));
			}
		};

		const clearUploadedList = (listType) => {
			setUploadStates((prev) => ({
				...prev,
				[listType]: {
					isUploading: false,
					fileName: null,
					domains: [],
					error: null,
					isUploaded: false,
				},
			}));

			if (listType === "includeList") {
				if (onUploadIncludeList) {
					onUploadIncludeList([]);
				}
				handleUpdateFilter("uploadedDomains", {
					includeList: [],
					excludeList: uploadStates.blockedList.domains || [],
				});
			} else if (listType === "blockedList") {
				if (onUploadBlockedList) {
					onUploadBlockedList([]);
				}
				handleUpdateFilter("uploadedDomains", {
					includeList: uploadStates.includeList.domains || [],
					excludeList: [],
				});
			}
		};

		const clearAllCategories = useCallback(() => {
			handleUpdateFilter("categories", []);
		}, [handleUpdateFilter]);

		const handleClearAllFilters = useCallback(() => {
			// ✅ NEW: Save scroll position before clearing
			saveScrollPosition();

			if (onClearAllFilters) {
				onClearAllFilters();
			} else {
				const defaultFilters = {
					mediaSubtype: ["Display"],
					deviceTypes: ["phone", "tablet", "mobile/desktop", "desktop"],
					videoPlacement: [],
					geography: [],
					dateRange: 30,
					contentStyle: [],
					content_style_1: [],
					content_style_2: [],
					content_style_3: [],
					safetyTier: [],
					safety_tier_1: [],
					safety_tier_2: [],
					safety_tier_3: [],
					minViewability: 0,
					minReach: 0,
					isDirect: false,
					publishers: [],
					selectedCountries: [],
					selectedCategories: [],
					limit: 1000,
					customFilters: [
						{
							id: Date.now(),
							column: "cprmarketrequests",
							threshold: 10000,
						},
						{
							id: Date.now() + 1,
							column: "impressions",
							threshold: 100,
						},
					],
					uploadedDomains: {
						includeList: [],
						excludeList: [],
					},
				};

				setLocalFilters(defaultFilters);

				setUploadStates({
					includeList: {
						isUploading: false,
						fileName: null,
						domains: [],
						error: null,
						isUploaded: false,
					},
					blockedList: {
						isUploading: false,
						fileName: null,
						domains: [],
						error: null,
						isUploaded: false,
					},
				});

				if (onFiltersChange) {
					onFiltersChange(defaultFilters);
				}
			}
		}, [onClearAllFilters, onFiltersChange, saveScrollPosition]);

		const updateCustomFilter = useCallback(
			(id, field, value) => {
				const currentFilters = ensuredFilters.customFilters || [];

				// Columns that need to be multiplied by 10^6
				const columnsToMultiply = [
					"nonzerobidsvalueinusd",
					"bidspendinusd",
					"revenueperimpression",
					"costperimpression",
					"bid_guidance",
					"bidCpm",
				];

				const updatedFilters = currentFilters.map((filter) => {
					if (filter.id === id) {
						const updatedFilter = { ...filter, [field]: value };

						// If updating threshold for columns that need multiplication
						if (
							field === "threshold" &&
							columnsToMultiply.includes(filter.column)
						) {
							return {
								...updatedFilter,
								threshold: value * 10 ** 6,
							};
						}

						return updatedFilter;
					}
					return filter;
				});

				handleUpdateFilter("customFilters", updatedFilters);
			},
			[ensuredFilters.customFilters, handleUpdateFilter],
		);

		const addCustomFilter = useCallback(() => {
			const currentFilters = ensuredFilters.customFilters || [];
			const newFilter = {
				id: Date.now() + Math.random(),
				column: "cprmarketrequests",
				threshold: 0,
			};

			const updatedFilters = [...currentFilters, newFilter];
			handleUpdateFilter("customFilters", updatedFilters);
		}, [ensuredFilters.customFilters, handleUpdateFilter]);

		const removeCustomFilter = useCallback(
			(id) => {
				const currentFilters = ensuredFilters.customFilters || [];
				const filterToRemove = currentFilters.find(
					(filter) => filter.id === id,
				);

				// Check if this is a default filter (cprmarketrequests or impressions)
				const isDefaultFilter =
					filterToRemove &&
					(filterToRemove.column === "cprmarketrequests" ||
						filterToRemove.column === "impressions");

				if (isDefaultFilter) {
					// For default filters, just reset threshold to 0 instead of removing
					const updatedFilters = currentFilters.map((filter) =>
						filter.id === id ? { ...filter, threshold: 0 } : filter,
					);
					handleUpdateFilter("customFilters", updatedFilters);
				} else {
					// For user-added filters, remove them completely
					const updatedFilters = currentFilters.filter(
						(filter) => filter.id !== id,
					);
					handleUpdateFilter("customFilters", updatedFilters);
				}
			},
			[ensuredFilters.customFilters, handleUpdateFilter],
		);

		const toggleCategory = (category) => {
			handleToggleCategory(category);
		};

		const toggleCategoryGroup = (groupName) => {
			setExpandedCategoryGroups((prev) => ({
				...prev,
				[groupName]: !prev[groupName],
			}));
		};

		const toggleCountryGroup = (groupName) => {
			setExpandedCountryGroups((prev) => ({
				...prev,
				[groupName]: !prev[groupName],
			}));
		};

		if (!showFilters) return null;

		const countryPresets = {
			US: {
				label: "United States",
				countries: ["US"],
			},
			UK: {
				label: "United Kingdom",
				countries: ["GB"],
			},
			APAC: {
				label: "Asia Pacific",
				countries: [
					"AU",
					"JP",
					"KR",
					"SG",
					"HK",
					"TW",
					"TH",
					"MY",
					"ID",
					"PH",
					"VN",
					"IN",
					"CN",
					"NZ",
				],
			},
		};

		const allCountryCodes = Object.keys(COUNTRY_CODES_MAP).filter((code) => {
			const countryInfo = getCountryInfo(code);
			return countryInfo.region && countryInfo.region.trim() !== "";
		});

		const selectedCountries = filters?.selectedCountries || [];
		const selectedCategories = filters?.categories || [];

		const handleCountrySelect = (countryCode) => {
			const newSelected = selectedCountries.includes(countryCode)
				? selectedCountries.filter((code) => code !== countryCode)
				: [...selectedCountries, countryCode];

			handleUpdateFilter("selectedCountries", newSelected);
		};

		const handlePresetSelection = (presetKey) => {
			const preset = countryPresets[presetKey];
			const currentSelection = selectedCountries;

			const allSelected = preset.countries.every((country) =>
				currentSelection.includes(country),
			);

			if (allSelected) {
				const newSelected = currentSelection.filter(
					(country) => !preset.countries.includes(country),
				);
				handleUpdateFilter("selectedCountries", newSelected);
			} else {
				const newSelected = [
					...new Set([...currentSelection, ...preset.countries]),
				];
				handleUpdateFilter("selectedCountries", newSelected);
			}
		};

		const isPresetSelected = (presetKey) => {
			const preset = countryPresets[presetKey];
			return preset.countries.every((country) =>
				selectedCountries.includes(country),
			);
		};

		const isPresetPartiallySelected = (presetKey) => {
			const preset = countryPresets[presetKey];
			const selectedInPreset = preset.countries.filter((country) =>
				selectedCountries.includes(country),
			);
			return (
				selectedInPreset.length > 0 &&
				selectedInPreset.length < preset.countries.length
			);
		};

		const handleSelectAllCountries = () => {
			const allSelected = selectedCountries.length === allCountryCodes.length;

			if (allSelected) {
				handleUpdateFilter("selectedCountries", []);
			} else {
				handleUpdateFilter("selectedCountries", [...allCountryCodes]);
			}
		};

		const CategorySection = () => {
			const searchResults = filteredCategories;

			return (
				<CategoryHeader
					title="Category"
					isExpanded={expandedCategories.category}
					onClick={() => handleToggleCategory("category")}
				>
					<CategorySearchInput
						value={categorySearchInput}
						onChange={handleCategorySearchChange}
						onClear={clearCategorySearch}
					/>

					{selectedCategories.length > 0 && (
						<div className="selection-summary">
							<div className="summary-header">
								<span className="summary-count">
									{selectedCategories.length} selected
								</span>
								<button
									onClick={() => handleUpdateFilter("categories", [])}
									className="clear-all-btn"
								>
									Clear All
								</button>
							</div>
							<div className="selected-items">
								<div className="selected-tags">
									{selectedCategories.map((cat) => (
										<span key={cat} className="selected-tag">
											{cat}
											<button
												onClick={() => {
													const newSelected = selectedCategories.filter(
														(c) => c !== cat,
													);
													handleUpdateFilter("categories", newSelected);
												}}
												className="remove-tag-btn"
												type="button"
											>
												×
											</button>
										</span>
									))}
								</div>
							</div>
						</div>
					)}

					{searchResults && (
						<div className="search-results">
							<div className="search-results-header">
								Found {searchResults.length} categories matching "
								{debouncedCategorySearch}"
							</div>
							<div className="search-results-list">
								{searchResults.slice(0, 50).map((category) => {
									const isSelected = selectedCategories.includes(category);

									return (
										<div
											key={category}
											className={`category-item ${isSelected ? "selected" : ""}`}
											onClick={() => {
												const newSelected = selectedCategories.includes(
													category,
												)
													? selectedCategories.filter((cat) => cat !== category)
													: [...selectedCategories, category];
												handleUpdateFilter("categories", newSelected);
											}}
										>
											<input
												type="checkbox"
												checked={isSelected}
												onChange={(e) => {
													e.stopPropagation();
													const newSelected = selectedCategories.includes(
														category,
													)
														? selectedCategories.filter(
																(cat) => cat !== category,
															)
														: [...selectedCategories, category];
													handleUpdateFilter("categories", newSelected);
												}}
												className="category-checkbox"
											/>
											<span className="item-name">{category}</span>
											{isSelected && <span className="check-mark">✓</span>}
										</div>
									);
								})}
								{searchResults.length > 50 && (
									<div className="too-many-results">
										Showing first 50 results. Please refine your search.
									</div>
								)}
							</div>
						</div>
					)}

					{!searchResults && Object.keys(processedCategories).length > 0 && (
						<div className="category-groups">
							{Object.entries(processedCategories).map(
								([groupName, categories]) => {
									const isExpanded = expandedCategoryGroups[groupName];
									const groupSelectedCount = categories.filter((cat) =>
										selectedCategories.includes(cat),
									).length;
									const allGroupSelected =
										categories.length > 0 &&
										categories.every((cat) => selectedCategories.includes(cat));
									const someGroupSelected =
										groupSelectedCount > 0 &&
										groupSelectedCount < categories.length;

									return (
										<div key={groupName} className="category-group">
											<div
												className="category-group-header"
												onClick={() => toggleCategoryGroup(groupName)}
											>
												<div className="group-info">
													<div className="group-title-row">
														<input
															type="checkbox"
															checked={allGroupSelected}
															ref={(el) => {
																if (el) el.indeterminate = someGroupSelected;
															}}
															onChange={(e) => {
																e.stopPropagation();

																if (allGroupSelected) {
																	const newSelected = selectedCategories.filter(
																		(cat) => !categories.includes(cat),
																	);
																	handleUpdateFilter("categories", newSelected);
																} else {
																	const newSelected = [
																		...new Set([
																			...selectedCategories,
																			...categories,
																		]),
																	];
																	handleUpdateFilter("categories", newSelected);
																}
															}}
															className="group-checkbox"
														/>
														<span className="group-name">{groupName}</span>
													</div>
													<span className="group-count">
														({categories.length} categories)
														{groupSelectedCount > 0 && (
															<span className="selected-count">
																{" "}
																• {groupSelectedCount} selected
															</span>
														)}
													</span>
												</div>
												<div
													className={`group-chevron ${isExpanded ? "expanded" : "collapsed"}`}
												>
													<ChevronDownIcon />
												</div>
											</div>

											{isExpanded && (
												<div className="category-group-content">
													<div className="categories-list">
														{categories.map((category) => {
															const isSelected =
																selectedCategories.includes(category);

															return (
																<div
																	key={category}
																	className={`category-item ${isSelected ? "selected" : ""}`}
																	onClick={() => {
																		const newSelected =
																			selectedCategories.includes(category)
																				? selectedCategories.filter(
																						(cat) => cat !== category,
																					)
																				: [...selectedCategories, category];
																		handleUpdateFilter(
																			"categories",
																			newSelected,
																		);
																	}}
																>
																	<input
																		type="checkbox"
																		checked={isSelected}
																		onChange={(e) => {
																			e.stopPropagation();
																			const newSelected =
																				selectedCategories.includes(category)
																					? selectedCategories.filter(
																							(cat) => cat !== category,
																						)
																					: [...selectedCategories, category];
																			handleUpdateFilter(
																				"categories",
																				newSelected,
																			);
																		}}
																		className="category-checkbox"
																	/>
																	<span className="item-name">{category}</span>
																	{isSelected && (
																		<span className="check-mark">✓</span>
																	)}
																</div>
															);
														})}
													</div>
												</div>
											)}
										</div>
									);
								},
							)}
						</div>
					)}

					{Object.keys(processedCategories).length === 0 && !searchResults && (
						<div className="no-categories">
							<p>
								No categories available. Please check your connection and try
								again.
							</p>
							<button onClick={retry} className="retry-btn">
								Retry Loading Categories
							</button>
						</div>
					)}
				</CategoryHeader>
			);
		};

		const CategoryHeader = ({
			title,
			isExpanded,
			onClick,
			children,
			showToggle = false,
			toggleValue = false,
			onToggleChange = null,
		}) => (
			<div className="filter-category-box">
				<div
					className={`category-header ${isExpanded ? "expanded" : "collapsed"}`}
					onClick={onClick}
				>
					<h3 className="category-title-new">
						{title}
						{showToggle && (
							<span
								className="header-toggle-container"
								onClick={(e) => e.stopPropagation()}
							>
								<label className="header-toggle-label">
									<input
										type="checkbox"
										checked={toggleValue}
										onChange={(e) =>
											onToggleChange && onToggleChange(e.target.checked)
										}
										className="header-toggle-input"
									/>
									<span className="header-toggle-slider"></span>
								</label>
							</span>
						)}
					</h3>
					<div
						className={`category-chevron ${isExpanded ? "expanded" : "collapsed"}`}
					>
						<ChevronDownIcon />
					</div>
				</div>
				{isExpanded && (
					<div
						className="category-content"
						style={{ paddingLeft: "8px", paddingRight: "8px" }}
					>
						{children}
					</div>
				)}
			</div>
		);

		return (
			<div className="filters-sidebar">
				<div className="filters-header">
					<h2 className="filters-title" style={{ fontSize: "16px" }}>
						Domain Filters
					</h2>
					<div className="filters-actions">
						<button
							className="btn-medium-primary"
							onClick={handleClearAllFilters}
							style={{ fontSize: "12px" }}
						>
							Clear All
						</button>
						<button onClick={onClose} className="close-btn">
							<BurgerIcon />
						</button>
					</div>
				</div>

				<div className="filters-content" ref={filtersContentRef}>
					{/* Targeting Filters */}
					<CategoryHeader
						title="Targeting Filters"
						isExpanded={expandedCategories.targeting}
						onClick={() => handleToggleCategory("targeting")}
					>
						<div className="filter-section">
							<SectionHeader
								title="Media Subtype"
								isExpanded={expandedSections.mediaSubtype}
								onToggle={() => handleToggleSection("mediaSubtype")}
								hasActiveFilters={(filters?.mediaSubtype?.length || 0) > 0}
								style={{ fontSize: "12px" }}
							/>
							{expandedSections.mediaSubtype && (
								<div className="section-content">
									<div className="media-subtype-tags">
										{((availableOptions?.mediaSubtype?.length || 0) > 0
											? availableOptions.mediaSubtype
											: [
													"Video",
													"Native",
													"Display",
													"Video+Native",
													"Display+Video",
													"Display+Native",
													"Display+Video+Native",
												]
										).map((type) => (
											<span
												key={type}
												className={`tag tag-clickable ${(filters?.mediaSubtype || []).includes(type) ? "tag-selected" : "tag-gray"}`}
												onClick={() =>
													handleToggleArrayFilter("mediaSubtype", type)
												}
												style={{ fontSize: "10px" }}
											>
												{type}
											</span>
										))}
									</div>
								</div>
							)}
						</div>

						<div className="filter-section">
							<SectionHeader
								title="Device Type"
								isExpanded={expandedSections.deviceTypes}
								onToggle={() => handleToggleSection("deviceTypes")}
								hasActiveFilters={(filters?.deviceTypes?.length || 0) > 0}
								style={{ fontSize: "12px" }}
							/>
							{expandedSections.deviceTypes && (
								<div className="section-content">
									<div className="device-type-tags">
										{((availableOptions?.deviceTypes?.length || 0) > 0
											? availableOptions.deviceTypes
											: [
													"desktop",
													"phone",
													"tablet",
													"set-top box",
													"connected tv",
													"mobile/tablet",
													"connected device",
												]
										).map((device) => (
											<span
												key={device}
												className={`tag tag-clickable ${(filters?.deviceTypes || []).includes(device) ? "tag-selected" : "tag-gray"}`}
												onClick={() =>
													handleToggleArrayFilter("deviceTypes", device)
												}
												style={{ fontSize: "10px" }}
											>
												{device}
											</span>
										))}
									</div>
								</div>
							)}
						</div>

						<div className="filter-section">
							<SectionHeader
								title="Video Placement"
								isExpanded={expandedSections.videoPlacement}
								onToggle={() => handleToggleSection("videoPlacement")}
								hasActiveFilters={(filters?.videoPlacement?.length || 0) > 0}
								style={{ fontSize: "12px" }}
							/>
							{expandedSections.videoPlacement && (
								<div className="section-content">
									<div className="video-placement-tags">
										{((availableOptions?.videoPlacement?.length || 0) > 0
											? availableOptions.videoPlacement
											: ["instream", "outstream", "rewarded", "interstitial"]
										).map((placement) => (
											<span
												key={placement}
												className={`tag tag-clickable ${(filters?.videoPlacement || []).includes(placement) ? "tag-selected" : "tag-gray"}`}
												onClick={() =>
													handleToggleArrayFilter("videoPlacement", placement)
												}
												style={{ fontSize: "10px" }}
											>
												{placement}
											</span>
										))}
									</div>
								</div>
							)}
						</div>

						<div className="filter-section">
							<SectionHeader
								title="Geography"
								isExpanded={expandedSections.geography}
								onToggle={() => handleToggleSection("geography")}
								hasActiveFilters={(selectedCountries?.length || 0) > 0}
								style={{ fontSize: "12px" }}
							/>
							{expandedSections.geography && (
								<div className="section-content">
									<div className="geography-section">
										<div className="geography-search">
											<CountrySearchInput
												value={countrySearchInput}
												onChange={handleCountrySearchChange}
												onClear={clearCountrySearch}
											/>
										</div>

										{debouncedCountrySearch && filteredCountryCodes && (
											<div className="geography-search-results">
												<div className="search-results-header">
													<span style={{ fontSize: "10px", color: "#6b7280" }}>
														Found {filteredCountryCodes.length} countries
														matching "{debouncedCountrySearch}"
													</span>
												</div>
												<div className="search-results-list">
													{filteredCountryCodes.map((countryCode) => {
														const countryInfo = getCountryInfo(countryCode);
														const isSelected =
															selectedCountries.includes(countryCode);
														return (
															<label
																key={countryCode}
																className="checkbox-label country-search-result"
																style={{ fontSize: "10px" }}
															>
																<input
																	type="checkbox"
																	checked={isSelected}
																	onChange={() =>
																		handleCountrySelect(countryCode)
																	}
																/>
																<span className="checkbox-text">
																	{countryInfo.name}
																	<span className="country-code-hint">
																		({countryCode})
																	</span>
																</span>
															</label>
														);
													})}
													{filteredCountryCodes.length === 0 && (
														<div
															className="no-countries-found"
															style={{ fontSize: "10px" }}
														>
															No countries found matching "
															{debouncedCountrySearch}"
														</div>
													)}
												</div>
											</div>
										)}

										{!debouncedCountrySearch && (
											<>
												<div className="presets-buttons">
													{Object.entries(countryPresets).map(
														([key, preset]) => {
															const isSelected = isPresetSelected(key);
															const isPartial = isPresetPartiallySelected(key);

															return (
																<button
																	key={key}
																	className={`preset-btn ${isSelected ? "selected" : ""} ${isPartial ? "partial" : ""}`}
																	onClick={() => handlePresetSelection(key)}
																	type="button"
																>
																	<span className="preset-label">
																		{preset.label}
																	</span>
																	{isSelected && (
																		<span className="preset-status">✓</span>
																	)}
																	{isPartial && (
																		<span className="preset-status">◐</span>
																	)}
																</button>
															);
														},
													)}
												</div>

												<div className="geography-global">
													<label
														className="checkbox-label global-checkbox"
														style={{ fontSize: "11px" }}
													>
														<input
															type="checkbox"
															checked={
																selectedCountries.length ===
																allCountryCodes.length
															}
															ref={(el) => {
																if (el) {
																	el.indeterminate =
																		selectedCountries.length > 0 &&
																		selectedCountries.length <
																			allCountryCodes.length;
																}
															}}
															onChange={handleSelectAllCountries}
														/>
														<span className="checkbox-text">
															Select All Countries ({allCountryCodes.length})
														</span>
													</label>
												</div>

												<div className="countries-by-region">
													{sortedRegions.map((region) => {
														const regionCountries =
															countriesByRegion[region] || [];
														const isSelected =
															regionCountries.length > 0 &&
															regionCountries.every((code) =>
																selectedCountries.includes(code),
															);
														const isPartial =
															regionCountries.some((code) =>
																selectedCountries.includes(code),
															) && !isSelected;
														const isExpanded = expandedCountryGroups[region];

														return (
															<div key={region} className="region-group">
																<div
																	className={`region-header ${isSelected ? "selected" : ""} ${isPartial ? "partial" : ""}`}
																	onClick={() => toggleCountryGroup(region)}
																>
																	<div className="region-name">
																		<span className="region-title">
																			{region}
																		</span>
																		<span className="region-count">
																			({regionCountries.length} countries)
																		</span>
																	</div>
																	<div className="region-controls">
																		<span
																			className={`region-status ${isSelected ? "selected" : ""} ${isPartial ? "partial" : ""}`}
																		>
																			{isSelected ? "✓" : isPartial ? "◐" : ""}
																		</span>
																		<div
																			className={`region-chevron ${isExpanded ? "expanded" : "collapsed"}`}
																		>
																			<ChevronDownIcon />
																		</div>
																	</div>
																</div>

																{isExpanded && (
																	<div className="region-dropdown-content">
																		<div className="region-select-all">
																			<label
																				className="checkbox-label"
																				style={{ fontSize: "10px" }}
																			>
																				<input
																					type="checkbox"
																					checked={isSelected}
																					onChange={() => {
																						if (isSelected) {
																							const newSelected =
																								selectedCountries.filter(
																									(code) =>
																										!regionCountries.includes(
																											code,
																										),
																								);
																							handleUpdateFilter(
																								"selectedCountries",
																								newSelected,
																							);
																						} else {
																							const newSelected = [
																								...new Set([
																									...selectedCountries,
																									...regionCountries,
																								]),
																							];
																							handleUpdateFilter(
																								"selectedCountries",
																								newSelected,
																							);
																						}
																					}}
																				/>
																				<span className="checkbox-text">
																					Select All {region}
																				</span>
																			</label>
																		</div>
																		<div className="countries-list">
																			{regionCountries.map((countryCode) => {
																				const countryInfo =
																					getCountryInfo(countryCode);
																				const isCountrySelected =
																					selectedCountries.includes(
																						countryCode,
																					);
																				return (
																					<label
																						key={countryCode}
																						className="checkbox-label country-item"
																						style={{ fontSize: "10px" }}
																					>
																						<input
																							type="checkbox"
																							checked={isCountrySelected}
																							onChange={() =>
																								handleCountrySelect(countryCode)
																							}
																						/>
																						<span
																							className="checkbox-text"
																							title={countryCode}
																						>
																							{countryInfo.name}
																						</span>
																					</label>
																				);
																			})}
																		</div>
																	</div>
																)}
															</div>
														);
													})}
												</div>
											</>
										)}
									</div>
								</div>
							)}
						</div>
						<div className="filter-section">
							<div className="section-content">
								<div
									className="filter-row"
									style={{
										display: "flex",
										justifyContent: "space-between",
										alignItems: "center",
										padding: "8px 8px",
										marginBottom: "8px",
									}}
								>
									<span
										style={{
											fontSize: "12px",
											color: "var(--openx-text-primary)",
											fontWeight: "500",
										}}
									>
										Data Period
									</span>
									<div
										className="tab-selector"
										style={{
											display: "flex",
											backgroundColor: "var(--openx-surface)",
											border: "1px solid var(--openx-border)",
											borderRadius: "6px",
											overflow: "hidden",
										}}
									>
										<button
											type="button"
											onClick={() => handleUpdateFilter("period", 7)}
											style={{
												padding: "4px 12px",
												fontSize: "11px",
												border: "none",
												backgroundColor:
													filters?.period === 7
														? "var(--openx-primary)"
														: "transparent",
												color:
													filters?.period === 7
														? "white"
														: "var(--openx-text-primary)",
												cursor: "pointer",
												transition: "all 0.2s ease",
											}}
										>
											7 Days
										</button>
										<button
											type="button"
											onClick={() => handleUpdateFilter("period", 30)}
											style={{
												padding: "4px 12px",
												fontSize: "11px",
												border: "none",
												backgroundColor:
													filters?.period === 30 || !filters?.period
														? "var(--openx-primary)"
														: "transparent",
												color:
													filters?.period === 30 || !filters?.period
														? "white"
														: "var(--openx-text-primary)",
												cursor: "pointer",
												transition: "all 0.2s ease",
											}}
										>
											30 Days
										</button>
									</div>
								</div>
							</div>
						</div>
					</CategoryHeader>

					<CategorySection />

					{/* Content Specifications */}
					<CategoryHeader
						title="Content Specifications"
						isExpanded={expandedCategories.content}
						onClick={() => handleToggleCategory("content")}
					>
						<div className="filter-section">
							<SectionHeader
								title="Content Style"
								isExpanded={expandedSections.contentStyle}
								onToggle={() => handleToggleSection("contentStyle")}
								hasActiveFilters={(filters?.contentStyle?.length || 0) > 0}
								style={{ fontSize: "12px" }}
							/>
							{expandedSections.contentStyle && (
								<div className="section-content">
									<div className="content-style-tags">
										{((availableOptions?.contentStyle?.length || 0) > 0
											? availableOptions.contentStyle
											: [
													"Factual",
													"Editorial",
													"Promotional",
													"Educational",
													"Entertainment",
													"User-Generated",
													"Reference",
												]
										).map((style) => (
											<span
												key={style}
												className={`tag tag-clickable ${(filters?.contentStyle || []).includes(style) ? "tag-selected" : "tag-gray"}`}
												onClick={() =>
													handleToggleArrayFilter("contentStyle", style)
												}
												style={{ fontSize: "10px" }}
											>
												{style}
											</span>
										))}
									</div>
								</div>
							)}
						</div>

						<div className="filter-section">
							<SectionHeader
								title="Safety Tier"
								isExpanded={expandedSections.safetyTier}
								onToggle={() => handleToggleSection("safetyTier")}
								hasActiveFilters={(filters?.safetyTier?.length || 0) > 0}
								style={{ fontSize: "12px" }}
							/>
							{expandedSections.safetyTier && (
								<div className="section-content">
									<div className="safety-tier-tags">
										{((availableOptions?.safetyTier?.length || 0) > 0
											? availableOptions.safetyTier
											: ["Universal", "Standard", "Limited", "Restricted"]
										).map((tier) => (
											<span
												key={tier}
												className={`tag tag-clickable ${(filters?.safetyTier || []).includes(tier) ? "tag-selected" : "tag-gray"}`}
												onClick={() =>
													handleToggleArrayFilter("safetyTier", tier)
												}
												style={{ fontSize: "10px" }}
											>
												{tier}
											</span>
										))}
									</div>
								</div>
							)}
						</div>
					</CategoryHeader>

					{/* Performance Metrics Category */}
					<CategoryHeader
						title="Performance Metrics"
						isExpanded={expandedCategories.performance}
						onClick={() => handleToggleCategory("performance")}
					>
						{expandedCategories.performance && (
							<div className="category-content">
								<div className="slider-group">
									<DraggableSlider
										label="Min. Viewability"
										value={filters?.minViewability ?? 80}
										min={0}
										max={100}
										step={1}
										unit="%"
										onChange={(value) =>
											handleUpdateFilter("minViewability", value)
										}
									/>

									<DraggableSlider
										label="Min. Expected Reach"
										value={filters?.minReach ?? 10}
										min={0}
										max={100}
										step={0.5}
										unit=""
										onChange={(value) => handleUpdateFilter("minReach", value)}
									/>

									<div
										style={{
											borderTop: "1px solid #e0e0e0",
											paddingTop: "16px",
											marginTop: "16px",
										}}
									>
										<div
											style={{
												display: "flex",
												alignItems: "center",
												justifyContent: "space-between",
												marginBottom: "12px",
											}}
										>
											<label
												style={{
													fontSize: "11px",
													fontWeight: "600",
													margin: 0,
												}}
											>
												Custom Performance Filters
											</label>
											<button
												onClick={addCustomFilter}
												style={{
													background: "none",
													border: "1px solid #0076BD",
													borderRadius: "4px",
													color: "#0076BD",
													cursor: "pointer",
													padding: "4px 8px",
													fontSize: "10px",
													display: "flex",
													alignItems: "center",
													gap: "4px",
												}}
												title="Add Custom Filter"
											>
												<span style={{ fontSize: "12px", fontWeight: "bold" }}>
													+
												</span>
												Add Filter
											</button>
										</div>

										<div className="custom-filters-list">
											{(ensuredFilters.customFilters || []).map(
												(customFilter) => {
													return (
														<div
															key={customFilter.id}
															style={{
																display: "flex",
																alignItems: "flex-end",
																gap: "6px",
																marginBottom: "8px",
																padding: "6px",
																border: "1px solid #e0e0e0",
																borderRadius: "4px",
																backgroundColor: "#f9f9f9",
															}}
														>
															<div style={{ flex: "1" }}>
																<label
																	style={{
																		fontSize: "9px",
																		marginBottom: "2px",
																		display: "block",
																		color: "#666",
																	}}
																>
																	Column:
																</label>
																<select
																	value={
																		customFilter.column || "cprmarketrequests"
																	}
																	onChange={(e) =>
																		updateCustomFilter(
																			customFilter.id,
																			"column",
																			e.target.value,
																		)
																	}
																	style={{
																		width: "100%",
																		padding: "4px 6px",
																		fontSize: "10px",
																		border: "1px solid #ddd",
																		borderRadius: "3px",
																		backgroundColor: "white",
																	}}
																>
																	{availableColumns.map((column) => (
																		<option
																			key={column.value}
																			value={column.value}
																		>
																			{column.label}
																		</option>
																	))}
																</select>
															</div>

															<div style={{ flex: "1" }}>
																<label
																	style={{
																		fontSize: "9px",
																		marginBottom: "2px",
																		display: "block",
																		color: "#666",
																	}}
																>
																	Min. Value:
																</label>
																<CustomFilterInput
																	customFilter={customFilter}
																	onUpdate={updateCustomFilter}
																/>
															</div>

															<button
																onClick={() =>
																	removeCustomFilter(customFilter.id)
																}
																style={{
																	background: "none",
																	border: "1px solid #dc3545",
																	borderRadius: "3px",
																	color: "#dc3545",
																	cursor: "pointer",
																	padding: "4px 6px",
																	fontSize: "10px",
																	width: "24px",
																	height: "26px",
																}}
																title="Remove Filter"
															>
																×
															</button>
														</div>
													);
												},
											)}
										</div>

										{(!ensuredFilters.customFilters ||
											ensuredFilters.customFilters.length === 0) && (
											<div
												style={{
													fontSize: "10px",
													color: "#999",
													textAlign: "center",
													padding: "8px",
													fontStyle: "italic",
												}}
											>
												Click "Add Filter" to create custom performance filters
											</div>
										)}
									</div>
								</div>
							</div>
						)}
					</CategoryHeader>

					{/* Publishers */}
					<CategoryHeader
						title="Publishers"
						isExpanded={expandedCategories.publishers}
						onClick={() => toggleCategory("publishers")}
					>
						{expandedCategories.publishers && (
							<div className="section-content" style={{ padding: "12px 0" }}>
								<div
									className="filter-row"
									style={{
										display: "flex",
										justifyContent: "space-between",
										alignItems: "center",
										padding: "8px 8px",
										marginBottom: "12px",
									}}
								>
									<span
										style={{
											fontSize: "12px",
											color: "var(--openx-text-primary)",
											fontWeight: "500",
										}}
									>
										Is Direct
									</span>
									<div
										className="tab-selector"
										style={{
											display: "flex",
											backgroundColor: "var(--openx-surface)",
											border: "1px solid var(--openx-border)",
											borderRadius: "6px",
											overflow: "hidden",
										}}
									>
										<button
											type="button"
											onClick={() => handleUpdateFilter("isDirect", true)}
											style={{
												padding: "4px 12px",
												fontSize: "11px",
												border: "none",
												backgroundColor:
													filters?.isDirect === true
														? "var(--openx-primary)"
														: "transparent",
												color:
													filters?.isDirect === true
														? "white"
														: "var(--openx-text-primary)",
												cursor: "pointer",
												transition: "all 0.2s ease",
											}}
										>
											Yes
										</button>
										<button
											type="button"
											onClick={() => handleUpdateFilter("isDirect", false)}
											style={{
												padding: "4px 12px",
												fontSize: "11px",
												border: "none",
												backgroundColor:
													filters?.isDirect === false
														? "var(--openx-primary)"
														: "transparent",
												color:
													filters?.isDirect === false
														? "white"
														: "var(--openx-text-primary)",
												cursor: "pointer",
												transition: "all 0.2s ease",
											}}
										>
											No
										</button>
									</div>
								</div>

								<div className="publisher-selection">
									<div className="publisher-search">
										<PublisherSearchInput
											value={publisherSearchInput}
											onChange={handlePublisherSearchChange}
										/>
									</div>

									<div
										className="publisher-list"
										style={{ maxHeight: "150px", overflowY: "auto" }}
									>
										{filteredPublishers.map((pub) => (
											<label
												key={pub}
												className="checkbox-label"
												style={{ fontSize: "11px" }}
											>
												<input
													type="checkbox"
													checked={(filters?.publishers || []).includes(pub)}
													onChange={() =>
														handleToggleArrayFilter("publishers", pub)
													}
												/>
												<span className="checkbox-text">{pub}</span>
											</label>
										))}
										{filteredPublishers.length === 0 &&
											debouncedPublisherSearch && (
												<div
													style={{
														padding: "6px",
														color: "var(--openx-text-secondary)",
														fontSize: "11px",
														textAlign: "center",
													}}
												>
													No publishers found matching "
													{debouncedPublisherSearch}"
												</div>
											)}
									</div>
								</div>
							</div>
						)}
					</CategoryHeader>

					{/* Limit Input and Upload Section */}
					<div
						style={{
							marginTop: "16px",
							padding: "12px",
							borderTop: "1px solid #e5e7eb",
							backgroundColor: "#f9fafb",
						}}
					>
						<div style={{ marginBottom: "12px" }}>
							<label
								style={{
									display: "block",
									fontSize: "11px",
									fontWeight: "500",
									color: "#374151",
									marginBottom: "4px",
								}}
							>
								Result Limit:
							</label>
							<LimitInput
								value={filters?.limit || 1000}
								onChange={(value) => handleUpdateFilter("limit", value)}
							/>
						</div>

						<div
							style={{
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								gap: "4px",
								margin: "0 0 8px 0",
								position: "relative",
							}}
						>
							<p
								style={{
									margin: "0",
									fontSize: "11px",
									color: "#6b7280",
									textAlign: "center",
								}}
							>
								Upload CSV files for domain filtering
							</p>
							<div
								style={{ position: "relative", display: "inline-flex" }}
								onMouseEnter={() => setShowUploadTooltip(true)}
								onMouseLeave={() => setShowUploadTooltip(false)}
							>
								<div
									style={{
										display: "inline-flex",
										alignItems: "center",
										justifyContent: "center",
										width: "14px",
										height: "14px",
										borderRadius: "50%",
										backgroundColor: "#6b7280",
										color: "white",
										fontSize: "9px",
										fontWeight: "bold",
										cursor: "pointer",
									}}
								>
									i
								</div>

								{showUploadTooltip && (
									<div
										style={{
											position: "absolute",
											top: "-45px",
											left: "-50px",
											transform: "translateX(-50%)",
											background: "#333",
											color: "white",
											padding: "6px 8px",
											borderRadius: "4px",
											fontSize: "10px",
											width: "120px",
											textAlign: "center",
											lineHeight: "1.3",
											zIndex: 1000,
										}}
									>
										CSV file must contain a domain name column
									</div>
								)}
							</div>
						</div>

						<div style={{ display: "flex", gap: "8px", marginBottom: "8px" }}>
							<div style={{ flex: 1 }}>
								<input
									type="file"
									accept=".csv"
									onChange={(e) =>
										handleFileUpload(e.target.files[0], "includeList")
									}
									style={{ display: "none" }}
									id="include-upload"
									disabled={uploadStates.includeList.isUploading}
								/>
								<label
									htmlFor="include-upload"
									style={{
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										gap: "4px",
										width: "100%",
										padding: "6px 8px",
										background: uploadStates.includeList.isUploading
											? "#6b7280"
											: "#0076BD",
										color: "white",
										border: "none",
										borderRadius: "4px",
										fontSize: "10px",
										fontWeight: "500",
										cursor: uploadStates.includeList.isUploading
											? "not-allowed"
											: "pointer",
										transition: "all 0.2s",
										textDecoration: "none",
										boxSizing: "border-box",
									}}
								>
									{uploadStates.includeList.isUploading ? (
										<>⏳ Uploading...</>
									) : (
										<>
											<UploadIcon />
											Allow List
										</>
									)}
								</label>
							</div>

							<div style={{ flex: 1 }}>
								<input
									type="file"
									accept=".csv"
									onChange={(e) =>
										handleFileUpload(e.target.files[0], "blockedList")
									}
									style={{ display: "none" }}
									id="exclude-upload"
									disabled={uploadStates.blockedList.isUploading}
								/>
								<label
									htmlFor="exclude-upload"
									style={{
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										gap: "4px",
										width: "100%",
										padding: "6px 8px",
										background: uploadStates.blockedList.isUploading
											? "#6b7280"
											: "#dc2626",
										color: "white",
										border: "none",
										borderRadius: "4px",
										fontSize: "10px",
										fontWeight: "500",
										cursor: uploadStates.blockedList.isUploading
											? "not-allowed"
											: "pointer",
										transition: "all 0.2s",
										textDecoration: "none",
										boxSizing: "border-box",
									}}
								>
									{uploadStates.blockedList.isUploading ? (
										<>⏳ Uploading...</>
									) : (
										<>
											<UploadIcon />
											Block List
										</>
									)}
								</label>
							</div>
						</div>

						{/* Display uploaded file names with remove buttons */}
						{(uploadStates.includeList.isUploaded ||
							uploadStates.blockedList.isUploaded) && (
							<div
								style={{
									marginBottom: "8px",
									gap: "4px",
									display: "flex",
									flexDirection: "column",
								}}
							>
								{uploadStates.includeList.isUploaded && (
									<div
										style={{
											display: "flex",
											alignItems: "center",
											justifyContent: "space-between",
											padding: "4px 6px",
											backgroundColor: "#dcfce7",
											border: "1px solid #22c55e",
											borderRadius: "3px",
											fontSize: "9px",
										}}
									>
										<span style={{ color: "#15803d", fontWeight: "500" }}>
											✓ Allow List: {uploadStates.includeList.fileName} (
											{uploadStates.includeList.domains.length} domains)
										</span>
										<button
											onClick={() => clearUploadedList("includeList")}
											style={{
												background: "none",
												border: "none",
												color: "#dc2626",
												cursor: "pointer",
												padding: "2px",
												borderRadius: "2px",
												display: "flex",
												alignItems: "center",
												fontSize: "12px",
											}}
											title="Remove Allow List"
										>
											<RemoveIcon />
										</button>
									</div>
								)}

								{uploadStates.blockedList.isUploaded && (
									<div
										style={{
											display: "flex",
											alignItems: "center",
											justifyContent: "space-between",
											padding: "4px 6px",
											backgroundColor: "#fee2e2",
											border: "1px solid #dc2626",
											borderRadius: "3px",
											fontSize: "9px",
										}}
									>
										<span style={{ color: "#dc2626", fontWeight: "500" }}>
											✗ Block List: {uploadStates.blockedList.fileName} (
											{uploadStates.blockedList.domains.length} domains)
										</span>
										<button
											onClick={() => clearUploadedList("blockedList")}
											style={{
												background: "none",
												border: "none",
												color: "#dc2626",
												cursor: "pointer",
												padding: "2px",
												borderRadius: "2px",
												display: "flex",
												alignItems: "center",
												fontSize: "12px",
											}}
											title="Remove Block List"
										>
											<RemoveIcon />
										</button>
									</div>
								)}
							</div>
						)}

						{/* Error Messages */}
						{(uploadStates.includeList.error ||
							uploadStates.blockedList.error) && (
							<div
								style={{
									fontSize: "9px",
									color: "#dc2626",
									textAlign: "center",
									backgroundColor: "#fef2f2",
									padding: "4px 6px",
									borderRadius: "3px",
									border: "1px solid #fecaca",
								}}
							>
								{uploadStates.includeList.error ||
									uploadStates.blockedList.error}
							</div>
						)}
					</div>
				</div>

				<style jsx>{`
        /* ✅ NEW: Scroll position preservation styles */
        .filters-content {
          scroll-behavior: auto !important; /* Disable smooth scrolling during programmatic scroll */
          overflow-x: hidden;
          overflow-y: auto;
          height: calc(100vh - 120px);
          position: relative;
        }

        /* ✅ NEW: Prevent layout shifts that cause scroll issues */
        .filter-section,
        .category-group,
        .region-group {
          contain: layout style; /* CSS containment for better performance */
        }

        /* ✅ NEW: Ensure consistent heights to prevent scroll jumps */
        .section-content,
        .category-content,
        .category-group-content,
        .region-dropdown-content {
          min-height: 0; /* Prevent flex grow issues */
        }

        /* Enhanced slider styles for easier dragging */
        .enhanced-slider-container {
          margin-bottom: 5px;
          user-select: none;
          -webkit-user-select: none;
          -moz-user-select: none;
          -ms-user-select: none;
          padding: 0 12px;
          will-change: transform;
        }

        .enhanced-slider-label {
          display: block;
          margin-bottom: 12px;
          font-size: 11px;
          font-weight: 600;
          color: #374151;
          user-select: none;
          pointer-events: none;
        }

        .enhanced-slider-wrapper {
          position: relative;
          padding: 12px 0;
          will-change: transform;
        }

        .enhanced-slider-track {
          position: relative;
          width: 100%;
          height: 3px; 
          background: #e5e7eb;
          border-radius: 4px;
          cursor: pointer;
          transition: all 0.15s ease;
          outline: none;
          overflow: visible;
          padding: 8px 0;
          margin: -8px 0;
          -webkit-touch-callout: none;
          -webkit-user-select: none;
          -khtml-user-select: none;
          -moz-user-select: none;
          -ms-user-select: none;
          user-select: none;
          -webkit-tap-highlight-color: transparent;
          touch-action: none;
          will-change: transform, background-color;
        }

        .enhanced-slider-track:hover {
          background: #d1d5db;
          box-shadow: 0 2px 4px rgba(0, 118, 189, 0.2);
        }

        .enhanced-slider-track:focus {
          box-shadow: 0 0 0 3px rgba(0, 118, 189, 0.3);
          outline: none;
        }

        .enhanced-slider-track:focus-visible {
          box-shadow: 0 0 0 3px rgba(0, 118, 189, 0.4);
          outline: 2px solid transparent;
        }

        .enhanced-slider-track.dragging {
          cursor: grabbing !important;
          box-shadow: 0 4px 8px rgba(0, 118, 189, 0.3);
          user-select: none !important;
          -webkit-user-select: none !important;
          background: #d1d5db;
        }

        .enhanced-slider-fill {
          position: absolute;
          left: 0;
          top: 8px;
          height: 6px;
          background: linear-gradient(135deg, #0076BD 0%, #0056b3 100%);
          border-radius: 4px;
          transition: none;
          pointer-events: none;
          user-select: none;
          max-width: 100%;
          will-change: width;
        }

        .enhanced-slider-thumb {
          position: absolute;
          top: 50%;
          width: 18px;
          height: 18px;
          background: #0076BD;
          border: 3px solid white;
          border-radius: 50%;
          cursor: grab;
          transform: translate(-50%, -50%);
          transition: box-shadow 0.15s ease, background-color 0.15s ease;
          box-shadow: 0 2px 6px rgba(0, 118, 189, 0.4);
          z-index: 10;
          user-select: none;
          pointer-events: auto;
          will-change: transform, background-color;
        }

        .enhanced-slider-thumb::before {
          content: '';
          position: absolute;
          top: -15px;
          left: -15px;
          right: -15px;
          bottom: -15px;
          border-radius: 50%;
          background: transparent;
          cursor: grab;
        }

        .enhanced-slider-thumb:hover {
          background: #0056b3;
          transform: translate(-50%, -50%) scale(1.1);
          box-shadow: 0 3px 8px rgba(0, 118, 189, 0.5);
          transition: all 0.15s ease;
        }

        .enhanced-slider-thumb.dragging {
          cursor: grabbing !important;
          background: #003d82;
          transform: translate(-50%, -50%) scale(1.15);
          box-shadow: 0 4px 12px rgba(0, 118, 189, 0.6);
          transition: none !important;
        }

        .enhanced-slider-thumb.dragging::before {
          cursor: grabbing !important;
        }

        .enhanced-slider-labels {
          display: flex;
          justify-content: space-between;
          margin-top: 8px;
          font-size: 10px;
          color: #6b7280;
          font-weight: 500;
          user-select: none;
          pointer-events: none;
        }

        .slider-group {
          -webkit-user-select: none;
          -moz-user-select: none;
          -ms-user-select: none;
          user-select: none;
          will-change: transform;
        }

        .enhanced-slider-track:not(.dragging):hover .enhanced-slider-thumb {
          background: #0056b3;
          transform: translate(-50%, -50%) scale(1.05);
        }

        .enhanced-slider-track:not(.dragging):active .enhanced-slider-thumb {
          transform: translate(-50%, -50%) scale(1.1);
        }

        .enhanced-slider-container input[type="range"] {
          display: none !important;
        }

        .enhanced-slider-track *,
        .enhanced-slider-thumb,
        .enhanced-slider-fill {
          -webkit-user-drag: none;
          -khtml-user-drag: none;
          -moz-user-drag: none;
          -o-user-drag: none;
          user-drag: none;
        }

        @media (max-width: 768px) {
          .enhanced-slider-thumb {
            width: 22px;
            height: 22px;
            border-width: 3px;
          }
          
          .enhanced-slider-thumb::before {
            top: -18px;
            left: -18px;
            right: -18px;
            bottom: -18px;
          }
          
          .enhanced-slider-track {
            height: 8px;
            padding: 12px 0;
            margin: -12px 0;
          }
          
          .enhanced-slider-fill {
            top: 12px;
            height: 8px;
          }
          
          .enhanced-slider-wrapper {
            padding: 15px 0;
          }
        }

        .enhanced-slider-track.dragging .enhanced-slider-fill {
          pointer-events: none;
          background: linear-gradient(135deg, #003d82 0%, #002a5c 100%);
        }

        /* Upload Tooltip Styles */
        .upload-info-icon {
          transition: all 0.2s ease;
        }

        .upload-info-icon:hover {
          background-color: #374151 !important;
          transform: scale(1.1);
        }

        .upload-tooltip {
          pointer-events: none;
          animation: fadeInTooltip 0.2s ease-in-out;
        }

        @keyframes fadeInTooltip {
          from {
            opacity: 0;
            transform: translateX(-50%) translateY(2px);
          }
          to {
            opacity: 1;
            transform: translateX(-50%) translateY(0);
          }
        }

        /* Geography Presets Styles */
        .presets-buttons {
          margin-bottom: 12px;
          display: flex;
          gap: 6px;
          flex-wrap: wrap;
        }

        .preset-btn {
          display: flex;
          align-items: center;
          gap: 4px;
          padding: 4px 8px;
          border: 1px solid #e5e7eb;
          border-radius: 4px;
          background: white;
          cursor: pointer;
          transition: all 0.2s;
          font-size: 10px;
          font-weight: 500;
          color: #374151;
          white-space: nowrap;
        }

        .preset-btn:hover {
          background: #f3f4f6;
          border-color: #d1d5db;
        }

        .preset-btn.selected {
          background: #0076BD;
          border-color: #0076BD;
          color: white;
        }

        .preset-btn.partial {
          background: rgba(0, 118, 189, 0.1);
          border-color: #0076BD;
          color: #0076BD;
        }

        .preset-flag {
          font-size: 12px;
        }

        .preset-label {
          font-size: 10px;
        }

        .preset-status {
          font-size: 10px;
          font-weight: bold;
        }

        /* Category Search Styles */
        .category-search-container {
          position: relative;
          margin-bottom: 12px;
        }

        .category-search-input {
          width: 100%;
          padding: 6px 25px 6px 8px;
          border: 1px solid #d1d5db;
          border-radius: 4px;
          font-size: 11px;
          background-color: white;
          transition: all 0.2s;
        }

        .category-search-input:focus {
          outline: none;
          border-color: #0076BD;
          box-shadow: 0 0 0 2px rgba(0, 118, 189, 0.1);
        }

        .clear-search-btn-category {
          position: absolute;
          right: 6px;
          top: 50%;
          transform: translateY(-50%);
          background: none;
          border: none;
          color: #6b7280;
          cursor: pointer;
          font-size: 16px;
          padding: 2px;
          border-radius: 2px;
        }

        .clear-search-btn-category:hover {
          color: #374151;
          background-color: #f3f4f6;
        }

        /* Search Input Styles */
        .search-container {
          position: relative;
          margin-bottom: 12px;
        }

        .search-input {
          width: 100%;
          padding: 6px 25px 6px 8px;
          border: 1px solid #d1d5db;
          border-radius: 4px;
          font-size: 11px;
          background-color: white;
          transition: all 0.2s;
        }

        .search-input:focus {
          outline: none;
          border-color: #0076BD;
          box-shadow: 0 0 0 2px rgba(0, 118, 189, 0.1);
        }

        .clear-search-btn {
          position: absolute;
          right: 6px;
          top: 50%;
          transform: translateY(-50%);
          background: none;
          border: none;
          color: #6b7280;
          cursor: pointer;
          font-size: 16px;
          padding: 2px;
          border-radius: 2px;
        }

        .clear-search-btn:hover {
          color: #374151;
          background-color: #f3f4f6;
        }

        /* Geography Section */
        .geography-section {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .geography-search {
          margin-bottom: 12px;
        }

        .geography-search-results {
          margin-bottom: 16px;
          border: 1px solid #e5e7eb;
          border-radius: 6px;
          background: white;
          overflow: hidden;
        }

        .search-results-header {
          padding: 8px 12px;
          background: #f8f9fa;
          border-bottom: 1px solid #e5e7eb;
        }

        .search-results-list {
          max-height: 300px;
          overflow-y: auto;
          padding: 8px;
        }

        .country-search-result {
          padding: 4px 8px;
          border-radius: 3px;
          margin-bottom: 2px;
        }

        .country-search-result:hover {
          background: rgba(0, 118, 189, 0.1);
        }

        .country-code-hint {
          color: #6b7280;
          font-size: 9px;
          margin-left: 4px;
        }

        .geography-global {
          margin-bottom: 12px;
          padding: 8px;
          background: #f0f9ff;
          border: 1px solid #0076BD;
          border-radius: 6px;
        }

        .global-checkbox {
          font-weight: 600;
          color: #0076BD;
        }

        /* Checkbox Label Styles */
        .checkbox-label {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 4px 6px;
          font-size: 10px;
          font-weight: 400;
          margin-bottom: 1px;
          cursor: pointer;
          border-radius: 3px;
          transition: all 0.2s;
        }

        .checkbox-text {
          color: inherit;
          font-size: 10px;
        }

        /* Countries by Region */
        .countries-by-region {
          max-height: 400px;
          overflow-y: auto;
        }

        .region-group {
          margin-bottom: 8px;
          border: 1px solid #e5e7eb;
          border-radius: 6px;
          background: white;
          overflow: hidden;
        }

        .region-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 8px 12px;
          cursor: pointer;
          transition: all 0.2s;
          background: #f8f9fa;
        }

        .region-header:hover {
          background: #e9ecef;
        }

        .region-header.selected {
          background: #0076BD;
          color: white;
        }

        .region-header.partial {
          background: rgba(0, 118, 189, 0.1);
          border-left: 3px solid #0076BD;
        }

        .region-name {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .region-title {
          font-size: 11px;
          font-weight: 600;
          color: inherit;
        }

        .region-count {
          font-size: 9px;
          color: inherit;
          opacity: 0.7;
        }

        .region-controls {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .region-status {
          font-size: 12px;
          font-weight: bold;
          width: 16px;
          text-align: center;
        }

        .region-status.selected {
          color: white;
        }

        .region-status.partial {
          color: #0076BD;
        }

        .region-chevron {
          transition: transform 0.2s ease;
          cursor: pointer;
          padding: 2px;
          border-radius: 2px;
        }

        .region-chevron:hover {
          background: rgba(0, 0, 0, 0.1);
        }

        .region-chevron.expanded {
          transform: rotate(0deg);
        }

        .region-chevron.collapsed {
          transform: rotate(-90deg);
        }

        .region-dropdown-content {
          border-top: 1px solid #e5e7eb;
          padding: 8px;
        }

        .region-select-all {
          padding: 4px 8px;
          background: #f8f9fa;
          border-radius: 4px;
          margin-bottom: 8px;
        }

        .countries-list {
          max-height: 200px;
          overflow-y: auto;
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .country-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 3px 8px;
          border-radius: 3px;
          cursor: pointer;
          transition: all 0.2s;
          font-size: 10px;
        }

        .country-item:hover {
          background: rgba(0, 118, 189, 0.1);
        }

        .country-item.selected {
          background: #0076BD;
          color: white;
        }

        .country-item.selected:hover {
          background: #0056b3;
        }

        .country-info {
          flex: 1;
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .country-name {
          font-weight: 500;
        }

        .country-code {
          color: #6b7280;
          font-size: 9px;
        }

        .country-item.selected .country-code {
          color: rgba(255, 255, 255, 0.8);
        }

        .no-countries-found {
          padding: 8px;
          color: #6b7280;
          text-align: center;
          font-style: italic;
          font-size: 11px;
        }

        /* Selection Summary */
        .selection-summary {
          background: #f8f9fa;
          border-radius: 6px;
          padding: 10px;
          margin-bottom: 12px;
          border-left: 4px solid #0076BD;
        }

        .summary-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 8px;
        }

        .summary-count {
          font-size: 11px;
          font-weight: 600;
          color: #0076BD;
        }

        .clear-all-btn {
          background: none;
          border: 1px solid #dc3545;
          color: #dc3545;
          padding: 2px 6px;
          border-radius: 3px;
          font-size: 9px;
          cursor: pointer;
          transition: all 0.2s;
        }

        .clear-all-btn:hover {
          background: #dc3545;
          color: white;
        }

        .tier-group {
          margin-bottom: 6px;
        }

        .tier-label {
          font-size: 10px;
          font-weight: 500;
          color: #6b7280;
          display: block;
          margin-bottom: 4px;
        }

        .selected-tags {
          display: flex;
          flex-wrap: wrap;
          gap: 4px;
        }

        .selected-tag {
          font-size: 9px;
          padding: 2px 6px;
          border-radius: 12px;
          background: #0076BD;
          color: white;
          font-weight: 500;
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .selected-tag.tier-1 {
          background: #28a745;
        }

        .selected-tag.tier-2 {
          background: #ffc107;
          color: #333;
        }

        .selected-tag.tier-3 {
          background: #17a2b8;
        }

        .remove-tag-btn {
          background: none;
          border: none;
          color: white;
          cursor: pointer;
          font-size: 12px;
          padding: 0;
          margin-left: 2px;
          border-radius: 2px;
          width: 14px;
          height: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .remove-tag-btn:hover {
          background: rgba(255, 255, 255, 0.2);
        }

        /* Search Results */
        .search-results {
          margin-bottom: 12px;
          border: 1px solid #e5e7eb;
          border-radius: 6px;
          background: white;
          overflow: hidden;
        }

        .search-results-header {
          font-size: 11px;
          font-weight: 600;
          color: #374151;
          padding: 8px 12px;
          background: #f8f9fa;
          border-bottom: 1px solid #e5e7eb;
        }

        .search-results-list {
          max-height: 200px;
          overflow-y: auto;
          padding: 8px;
        }

        .too-many-results {
          padding: 8px;
          color: #6b7280;
          text-align: center;
          font-style: italic;
          font-size: 10px;
          background: #f8f9fa;
          border-top: 1px solid #e5e7eb;
          margin: 0 -8px -8px -8px;
        }

        /* Category Items */
        .category-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 6px 8px;
          margin: 2px 0;
          border-radius: 4px;
          cursor: pointer;
          transition: all 0.2s;
          border: 1px solid transparent;
          font-size: 10px;
        }

        .category-item:hover {
          background: #f3f4f6;
          border-color: #d1d5db;
        }

        .category-item.selected {
          background: #0076BD;
          color: white;
          border-color: #0056b3;
        }

        .category-item.selected:hover {
          background: #0056b3;
        }

        .item-content {
          flex: 1;
        }

        .item-name {
          font-weight: 500;
        }

        .item-path {
          font-size: 9px;
          color: #6b7280;
          margin-top: 2px;
        }

        .check-mark {
          color: white;
          font-weight: bold;
          margin-left: 8px;
          font-size: 12px;
        }

        /* Category Groups */
        .category-groups {
          max-height: 400px;
          overflow-y: auto;
        }

        .category-group {
          margin-bottom: 8px;
          border: 1px solid #e5e7eb;
          border-radius: 6px;
          background: white;
          overflow: hidden;
        }

        .category-group-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 8px 12px;
          cursor: pointer;
          transition: all 0.2s;
          background: #f8f9fa;
          border-bottom: 1px solid #e5e7eb;
        }

        .category-group-header:hover {
          background: #e9ecef;
        }

        .group-info {
          display: flex;
          flex-direction: column;
          gap: 2px;
          flex: 1;
        }

        .group-name {
          font-size: 12px;
          font-weight: 600;
          color: #374151;
        }

        .group-count {
          font-size: 9px;
          color: #6b7280;
        }

        .selected-count {
          color: #0076BD;
          font-weight: 500;
        }

        .group-chevron {
          transition: transform 0.2s ease;
          cursor: pointer;
          padding: 2px;
          border-radius: 2px;
        }

        .group-chevron:hover {
          background: rgba(0, 0, 0, 0.1);
        }

        .group-chevron.expanded {
          transform: rotate(0deg);
        }

        .group-chevron.collapsed {
          transform: rotate(-90deg);
        }

        .category-group-content {
          padding: 8px;
        }

        .group-select-all {
          padding: 4px 8px;
          background: #f8f9fa;
          border-radius: 4px;
          margin-bottom: 8px;
          border: 1px solid #e9ecef;
        }

        .select-all-btn {
          background: none;
          border: 1px solid #0076BD;
          color: #0076BD;
          padding: 4px 8px;
          border-radius: 3px;
          font-size: 10px;
          cursor: pointer;
          transition: all 0.2s;
          font-weight: 500;
        }

        .select-all-btn:hover {
          background: #0076BD;
          color: white;
        }

        .categories-list {
          max-height: 200px;
          overflow-y: auto;
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .group-title-row {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .group-checkbox {
          width: 14px;
          height: 14px;
          cursor: pointer;
          accent-color: #0076BD;
        }

        .category-checkbox {
          width: 14px;
          height: 14px;
          cursor: pointer;
          margin-right: 8px;
          accent-color: #0076BD;
        }

        .group-checkbox:indeterminate {
          accent-color: #ffc107;
        }

        .selected-items {
          margin-top: 8px;
        }

        /* No Categories */
        .no-categories {
          padding: 20px;
          text-align: center;
          color: #6b7280;
          font-size: 11px;
        }

        .no-categories p {
          margin-bottom: 12px;
          line-height: 1.4;
        }

        .retry-btn {
          background: #0076BD;
          color: white;
          border: none;
          padding: 6px 12px;
          border-radius: 4px;
          font-size: 10px;
          cursor: pointer;
          transition: all 0.2s;
          font-weight: 500;
        }

        .retry-btn:hover {
          background: #0056b3;
        }

        /* Tag Styles */
        .content-style-tags,
        .media-subtype-tags,
        .device-type-tags,
        .video-placement-tags,
        .safety-tier-tags {
          display: flex;
          flex-wrap: wrap;
          gap: 4px;
        }

        .tag {
          font-size: 10px;
          padding: 4px 8px;
          border-radius: 12px;
          font-weight: 500;
          cursor: pointer;
          user-select: none;
          transition: all 0.2s;
        }

        .tag-clickable {
          cursor: pointer;
          transition: all 0.2s;
        }

        .tag-gray {
          background: #f3f4f6;
          color: #374151;
          border: 1px solid #e5e7eb;
        }

        .tag-selected {
          background: #0076BD;
          color: white;
          border: 1px solid #0076BD;
        }

        /* Header Toggle Styles */
        .header-toggle-container {
          margin-left: 12px;
          display: inline-flex;
          align-items: center;
        }

        .header-toggle-label {
          position: relative;
          display: inline-block;
          width: 34px;
          height: 18px;
          cursor: pointer;
        }

        .header-toggle-input {
          opacity: 0;
          width: 0;
          height: 0;
        }

        .header-toggle-slider {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: #ccc;
          border-radius: 18px;
          transition: .4s;
        }

        .header-toggle-slider:before {
          position: absolute;
          content: "";
          height: 14px;
          width: 14px;
          left: 2px;
          bottom: 2px;
          background-color: white;
          border-radius: 50%;
          transition: .4s;
        }

        .header-toggle-input:checked + .header-toggle-slider {
          background-color: #0076BD;
        }

        .header-toggle-input:checked + .header-toggle-slider:before {
          transform: translateX(16px);
        }

        .custom-filters-list {
          max-height: 200px;
          overflow-y: auto;
          border: 1px solid #e5e7eb;
          border-radius: 4px;
          background: white;
          padding: 4px;
        }

        .custom-filters-list:empty {
          border: none;
          background: transparent;
          padding: 0;
        }
      `}</style>
			</div>
		);
	},
	(prevProps, nextProps) => {
		// ✅ IMPROVED: Better comparison to prevent unnecessary re-renders
		if (prevProps.showFilters !== nextProps.showFilters) return false;
		if (prevProps.activeTab !== nextProps.activeTab) return false;
		if (prevProps.userId !== nextProps.userId) return false;

		// Deep comparison for filters object
		const prevFilters = prevProps.filters || {};
		const nextFilters = nextProps.filters || {};

		// Compare filter keys and values more efficiently
		const prevKeys = Object.keys(prevFilters);
		const nextKeys = Object.keys(nextFilters);

		if (prevKeys.length !== nextKeys.length) return false;

		for (const key of prevKeys) {
			const prevValue = prevFilters[key];
			const nextValue = nextFilters[key];

			// Handle arrays
			if (Array.isArray(prevValue) && Array.isArray(nextValue)) {
				if (prevValue.length !== nextValue.length) return false;
				if (!prevValue.every((item, index) => item === nextValue[index]))
					return false;
			}
			// Handle objects
			else if (
				typeof prevValue === "object" &&
				typeof nextValue === "object" &&
				prevValue !== null &&
				nextValue !== null
			) {
				if (JSON.stringify(prevValue) !== JSON.stringify(nextValue))
					return false;
			}
			// Handle primitives
			else if (prevValue !== nextValue) {
				return false;
			}
		}

		// Compare expanded sections
		const prevExpanded = prevProps.expandedSections || {};
		const nextExpanded = nextProps.expandedSections || {};
		const prevExpandedKeys = Object.keys(prevExpanded);
		const nextExpandedKeys = Object.keys(nextExpanded);

		if (prevExpandedKeys.length !== nextExpandedKeys.length) return false;

		for (const key of prevExpandedKeys) {
			if (prevExpanded[key] !== nextExpanded[key]) return false;
		}

		return true;
	},
);
